import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.min.css';
import template from '../assets/dailyReportTemplate.json';
import { registerAllModules } from 'handsontable/registry';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import './DispatchReportPage.css';
import { socket } from '../socket';
import { debounce } from 'lodash';
import { auth } from '../firebaseConfig';
import { Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, TextField } from '@mui/material';
import { formatAmountDisplay, safeAmountToNumber, processAmountInput } from '../utils/numberUtils';
import FullScreenButton from './FullScreenButton';
import SmsModal from './SmsModal';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouteIcon from '@mui/icons-material/Route';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import GroupsIcon from '@mui/icons-material/Groups';
import PaymentIcon from '@mui/icons-material/Payment';
import NoteIcon from '@mui/icons-material/Note';
import AddIcon from '@mui/icons-material/Add';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import IosShareIcon from '@mui/icons-material/IosShare';
import HomeIcon from '@mui/icons-material/Home';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';

interface DispatchReportPageProps {
  isFullScreen: boolean;
  setIsFullScreen: (v: boolean) => void;
  selectedDate?: string;
  setSelectedDate: (v: string) => void;
}

interface RowData {
  id: string;
  rowId: string;
  order: number;
  company?: string;
  vehicleNumber?: string;
  route?: string;
  itemName?: string;
  cargo?: string;
  billingAmount?: number;
  department?: string;
  contact?: string;
  paymentAmount?: number;
  businessNumber?: string;
  driverName?: string;
  distance?: number;
  weight?: number;
  remarks?: string;
  memo?: string;
  routeMemo?: string;
  profit?: number;
  settlementStatus?: string;
  settlementDate?: string | null;
  Receipt?: string;
  createdAt: string;
  updatedAt: string;
  customerMessage?: string; // 화주전달 추가
}

// 수출/내수 데이터 구조
interface ExportData {
  export: number;
  number: string;
}

interface DomesticData {
  domestic: number;
  shipment: number;
  number: string;
  dayNight?: string; // 주/야
}

interface SummaryData {
  exportData: ExportData[];
  domesticData: DomesticData[];
}

const COLUMN_HEADERS = [
  '거래처상호', '차량번호', '운송구간', '품명', '차종', '청구운송비', '소속', '연락처', '지불운송비', '사업자번호', '이름', '거리', '중량', '문자', '비고', '메모', '화주전달'
];
const colWidths = [160, 120, 220, 80, 70, 80, 80, 130, 80, 100, 60, 50, 50, 80, 110, 250, 200];

// 컬럼 인덱스 상수 (헤더 기반 계산)
const IDX = Object.freeze({
  company: COLUMN_HEADERS.indexOf('거래처상호'),
  vehicle: COLUMN_HEADERS.indexOf('차량번호'),
  route: COLUMN_HEADERS.indexOf('운송구간'),
  item: COLUMN_HEADERS.indexOf('품명'),
  cargo: COLUMN_HEADERS.indexOf('차종'),
  billing: COLUMN_HEADERS.indexOf('청구운송비'),
  department: COLUMN_HEADERS.indexOf('소속'),
  contact: COLUMN_HEADERS.indexOf('연락처'),
  payment: COLUMN_HEADERS.indexOf('지불운송비'),
  business: COLUMN_HEADERS.indexOf('사업자번호'),
  driver: COLUMN_HEADERS.indexOf('이름'),
  distance: COLUMN_HEADERS.indexOf('거리'),
  weight: COLUMN_HEADERS.indexOf('중량'),
  sms: COLUMN_HEADERS.indexOf('문자'),
  remarks: COLUMN_HEADERS.indexOf('비고'),
  memo: COLUMN_HEADERS.indexOf('메모'),
  customerMessage: COLUMN_HEADERS.indexOf('화주전달'),
  id: COLUMN_HEADERS.length,
  order: COLUMN_HEADERS.length + 1,
  updatedAt: COLUMN_HEADERS.length + 2,
  routeMemo: COLUMN_HEADERS.length + 3,
});

/** 운송구간 메모 추가/편집 가능 행: 거래처·rowId·운송구간 값 모두 있어야 함 */
function isRouteMemoEligibleRow(data: any[] | null | undefined): boolean {
  if (!data) return false;
  const company = String(data[IDX.company] ?? '').trim();
  const rowId = String(data[IDX.id] ?? '').trim();
  const route = String(data[IDX.route] ?? '').trim();
  return company !== '' && rowId !== '' && route !== '';
}

const ROUTE_MEMO_POPUP_WIDTH = 300;
const ROUTE_MEMO_VIEWPORT_MARGIN = 8;

function estimateRouteMemoPopupHeight(text: string): number {
  const lines = text.split('\n');
  let wrappedLines = 0;
  for (const line of lines) {
    wrappedLines += Math.max(1, Math.ceil(line.length / 26));
  }
  wrappedLines = Math.max(wrappedLines, 1);
  const bodyHeight = Math.min(240, Math.max(36, wrappedLines * 20));
  return 10 + 22 + 6 + bodyHeight + 8 + 28 + 10;
}

function computeRouteMemoHoverPosition(rect: DOMRect, text: string): { left: number; top: number } {
  const width = Math.min(ROUTE_MEMO_POPUP_WIDTH, window.innerWidth - ROUTE_MEMO_VIEWPORT_MARGIN * 2);
  const height = Math.min(
    estimateRouteMemoPopupHeight(text),
    window.innerHeight - ROUTE_MEMO_VIEWPORT_MARGIN * 2
  );

  let left = rect.right + 6;
  if (left + width > window.innerWidth - ROUTE_MEMO_VIEWPORT_MARGIN) {
    const leftOfCell = rect.left - width - 6;
    left = leftOfCell >= ROUTE_MEMO_VIEWPORT_MARGIN
      ? leftOfCell
      : Math.max(ROUTE_MEMO_VIEWPORT_MARGIN, window.innerWidth - width - ROUTE_MEMO_VIEWPORT_MARGIN);
  }

  let top = rect.top;
  if (top + height > window.innerHeight - ROUTE_MEMO_VIEWPORT_MARGIN) {
    top = rect.bottom - height;
  }
  top = Math.max(ROUTE_MEMO_VIEWPORT_MARGIN, top);
  if (top + height > window.innerHeight - ROUTE_MEMO_VIEWPORT_MARGIN) {
    top = Math.max(ROUTE_MEMO_VIEWPORT_MARGIN, window.innerHeight - height - ROUTE_MEMO_VIEWPORT_MARGIN);
  }

  return { left, top };
}

const COLUMN_FIELD_MAP: { [key: string]: string } = {
  '거래처상호': 'company',
  '차량번호': 'vehicleNumber',
  '운송구간': 'route',
  '품명': 'itemName',
  '차종': 'cargo',
  '청구운송비': 'billingAmount',
  '소속': 'department',
  '연락처': 'contact',
  '지불운송비': 'paymentAmount',
  '사업자번호': 'businessNumber',
  '이름': 'driverName',
  '거리': 'distance',
  '중량': 'weight',
  '비고': 'remarks',
  '메모': 'memo',
  '화주전달': 'customerMessage', // 추가
};

// 행 길이 보정 함수
function normalizeRowLength(row: any[], length: number) {
  const r = [...row];
  while (r.length < length) r.push('');
  return r.slice(0, length);
}

// 거래처명 비교를 위한 정규화 함수
function normalizeCompanyName(name: string) {
  if (!name) return '';
  return name
    .replace(/\(.*?\)/g, '') // 괄호 및 괄호 안 내용 제거
    .replace(/(주식회사|유한회사|유한|주|유|합|재|사)/gi, '') // 법인 구분어 제거
    .replace(/\s+/g, '') // 모든 공백 제거
    .toLowerCase()
    .trim();
}

// 연락처 하이픈 자동 변환 함수
function formatPhoneNumber(phone: string) {
  const onlyNum = phone.replace(/[^0-9]/g, '');
  if (onlyNum.length === 11) {
    return onlyNum.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  }
  if (onlyNum.length === 10) {
    return onlyNum.replace(/(\d{3})(\d{3,4})(\d{4})/, '$1-$2-$3');
  }
  return phone;
}

function getKoreanDayShort(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('ko-KR', { weekday: 'short' });
}

/** 차량번호에서 숫자만 추출한 끝 4자리가 일치하는 첫 행 (전화 문의 시 빠른 검색용) */
function findFirstRowByVehicleSuffix(rows: any[][], suffix4: string): number | null {
  const s = suffix4.replace(/[^0-9]/g, '').slice(0, 4);
  if (s.length !== 4) return null;
  for (let r = 0; r < rows.length; r++) {
    const raw = String(rows[r]?.[IDX.vehicle] ?? '').trim();
    if (!raw) continue;
    const digits = raw.replace(/[^0-9]/g, '');
    if (digits.length >= 4 && digits.endsWith(s)) return r;
  }
  return null;
}

// 요일별 색상 매핑
function getDayColor(dayKor: string) {
  const dayColorMap: { [key: string]: string } = {
    '월': '#ff9800', // 주황
    '화': '#388e3c', // 초록
    '수': '#00bcd4', // 청록
    '목': '#7b1fa2', // 보라
    '금': '#d32f2f', // 빨강
    '토': '#795548', // 갈색
    '일': '#f44336'  // 진빨강
  };
  return dayColorMap[dayKor] || '#1976d2';
}

const DispatchReportPage: React.FC<DispatchReportPageProps> = ({ isFullScreen, setIsFullScreen, selectedDate, setSelectedDate }) => {
  const skipRouteAutoRef = useRef(false); // 함수 내부로 이동
  // 표 데이터 상태만 남김
  const [tableDataState, setTableDataState] = useState<any[][]>([]);
  const [loading, setLoading] = useState(false);
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);
  const [exportValue, setExportValue] = useState('');
  const [domesticValue, setDomesticValue] = useState('');
  const [oilValue, setOilValue] = useState('');
  const [exportMemo, setExportMemo] = useState('');
  const [domesticMemo, setDomesticMemo] = useState('');
  const [oilMemo, setOilMemo] = useState('');
  /** 차량번호 끝 4자리 검색(전화 문의 시) */
  const [vehiclePlateSuffix, setVehiclePlateSuffix] = useState('');
  const [vehicleHighlightRow, setVehicleHighlightRow] = useState<number | null>(null);
  const [orderCount, setOrderCount] = useState(0);
  const [notAssignedCount, setNotAssignedCount] = useState(0);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<string[]>([]);
  const [companySearch, setCompanySearch] = useState('');
  const [blinkOffRows, setBlinkOffRows] = useState<Set<string>>(new Set());
  const [vehicleAutoList, setVehicleAutoList] = useState<any[]>([]);
  const [vehicleAutoRow, setVehicleAutoRow] = useState<number | null>(null);
  const [vehicleAutoCol, setVehicleAutoCol] = useState<number | null>(null);
  const [vehicleAutoBoxPos, setVehicleAutoBoxPos] = useState<{left: number, top: number} | null>(null);
  const hotRef = useRef<any>(null);
  const lastEventIdRef = useRef<string | null>(null);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const lastSelectionRef = useRef<any>(null);
  const [scratchpadContent, setScratchpadContent] = useState('');
  const [doodle, setDoodle] = useState('');
  const [doodle2, setDoodle2] = useState('');
  const [doodle3, setDoodle3] = useState('');
  // SMS 모달/상태 (중복 선언 방지)
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [smsRowId, setSmsRowId] = useState('');
  const [smsDefaultMsg, setSmsDefaultMsg] = useState('');
  const [smsStatus, setSmsStatus] = useState<Record<string, boolean>>({});
  const [smsSentListOpen, setSmsSentListOpen] = useState(false);
  const [smsSentListLoading, setSmsSentListLoading] = useState(false);
  const [smsSentListItems, setSmsSentListItems] = useState<any[]>([]);
  const [smsSentListError, setSmsSentListError] = useState<string | null>(null);
  const [smsSentListSubtitle, setSmsSentListSubtitle] = useState('');
  const [smsSentListCopySnackbar, setSmsSentListCopySnackbar] = useState(false);
  // 배차완료 상태 맵
  const [doneStatus, setDoneStatus] = useState<Record<string, boolean>>({});
  
  // 수출/내수 데이터 상태
  const [exportData, setExportData] = useState<ExportData[]>([]);
  const [domesticData, setDomesticData] = useState<DomesticData[]>([]);
  
  // 편집 중인 셀 추적
  const editingCellsRef = useRef<Set<string>>(new Set());

  // 운송구간 자동완성 상태 추가
  const [routeInput, setRouteInput] = useState('');
  const [routeSuggestions, setRouteSuggestions] = useState<any[]>([]);
  const [routeDropdownRow, setRouteDropdownRow] = useState<number | null>(null);
  const [routeDropdownPos, setRouteDropdownPos] = useState<{left: number, top: number} | null>(null);
  const routeInputRefs = useRef<{ [rowIdx: number]: HTMLInputElement | null }>({});

  // 1. summaryLoading 상태 추가
  const [summaryLoading, setSummaryLoading] = useState(false);

  // fetchId를 관리할 ref 추가
  const lastFetchIdRef = useRef<string | null>(null);

  // 오늘 날짜인지 여부: 복구/백업 UI 활성화용
  const todayStr = new Date().toISOString().slice(0, 10);
  const isSingleDateToday = useMemo(() => {
    if (!selectedDate) return false;
    const dates = selectedDate.split(',').map(d => d.trim()).filter(Boolean);
    return dates.length === 1 && dates[0] === todayStr;
  }, [selectedDate, todayStr]);

  // 배차일보 복구 모달 상태
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<{ id: string; createdAt: string | null; rowCount: number }[]>([]);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string>('');

  // selectedDate를 useRef로 관리
  const selectedDateRef = useRef(selectedDate);
  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  // debouncedFetch: selectedDate 의존성으로 useCallback
  const debouncedFetch = useCallback(
    debounce(async () => {
      const currentDate = selectedDate;
      if (!currentDate) return;
      setLoading(true);
      try {
        // 여러 날짜의 데이터를 가져와서 합치기
        const dates = currentDate.split(',').map(d => d.trim());
        const allRows: any[] = [];
        for (const date of dates) {
          const res = await fetch(`/api/dispatch-reports/${date}/rows`);
          const rows = await res.json();
          // 각 행에 날짜 정보 추가
          rows.forEach((row: any) => {
            row.date = date;
          });
          allRows.push(...rows);
        }
        // 날짜 기준 내림차순, 같은 날짜는 order 오름차순 정렬
        allRows.sort((a, b) => b.date.localeCompare(a.date) || (a.order || 0) - (b.order || 0));
        // SMS 상태/배차완료 상태 맵 저장
        const statusMap: Record<string, boolean> = {};
        allRows.forEach((r: any) => { if (r.id) statusMap[r.id] = !!r.sms; });
        setSmsStatus(statusMap);
        const doneMap: Record<string, boolean> = {};
        allRows.forEach((r: any) => { if (r.id) doneMap[r.id] = !!(r.completed || r.dispatchDone); });
        setDoneStatus(doneMap);

        setTableDataState(allRows.map((r: any) => [
          r.company || '', r.vehicleNumber || '', r.route || '', r.itemName || '', r.cargo || '',
          formatAmountDisplay(r.billingAmount), r.department || '', r.contact || '', formatAmountDisplay(r.paymentAmount),
          r.businessNumber || '', r.driverName || '', formatAmountDisplay(r.distance), formatAmountDisplay(r.weight),
          '', // 문자(UI 전용)
          r.remarks || '', r.memo || '', r.customerMessage || '',
          r.id || '', r.order || 0, r.updatedAt || '', r.routeMemo || ''
        ]));
        // 2. 데이터 갱신 후 포커스/선택 복원
        setTimeout(() => {
          const ht2 = hotRef.current?.hotInstance;
          if (ht2 && lastSelectionRef.current) {
            ht2.selectCell(...lastSelectionRef.current);
          }
        }, 0);
      } catch (error) {
        console.error('데이터 fetch 실패:', error);
      } finally {
        setLoading(false);
      }
    }, 300),
    [selectedDate]
  );

  // selectedDate가 바뀔 때 바로 로딩 시작 후 fetch
  useEffect(() => {
    if (!selectedDate) return;
    setLoading(true); // 바로 로딩 시작
    debouncedFetch();
  }, [selectedDate]);

  // 부분 업데이트 함수 추가 (타임스탬프 검증 포함)
  const updateSingleRow = useCallback((rowId: string, changedData: any, timestamp?: number) => {
    setTableDataState(prev => {
      const newData = [...prev];
      const rowIndex = newData.findIndex(row => row[IDX.id] === rowId);
      
      if (rowIndex !== -1) {
        const currentRow = newData[rowIndex];
        
        // 타임스탬프 검증 (서버 데이터가 더 최신인 경우만 업데이트)
        if (timestamp && currentRow[IDX.updatedAt]) {
          const serverTime = new Date(timestamp).getTime();
          const localTime = new Date(currentRow[IDX.updatedAt]).getTime();
          
          if (localTime > serverTime) {
            console.log('로컬 데이터가 더 최신입니다. 업데이트 무시');
            return prev;
          }
        }
        
        // 변경된 필드만 업데이트 (IDX 상수 사용으로 하드코딩 인덱스 오류 방지)
        const fieldMap = {
          company: IDX.company,
          vehicleNumber: IDX.vehicle,
          route: IDX.route,
          itemName: IDX.item,
          cargo: IDX.cargo,
          billingAmount: IDX.billing,
          department: IDX.department,
          contact: IDX.contact,
          paymentAmount: IDX.payment,
          businessNumber: IDX.business,
          driverName: IDX.driver,
          distance: IDX.distance,
          weight: IDX.weight,
          remarks: IDX.remarks,
          memo: IDX.memo,
          routeMemo: IDX.routeMemo,
          customerMessage: IDX.customerMessage,
        } as const;
        if (Object.prototype.hasOwnProperty.call(changedData, 'sms')) {
          setSmsStatus(prev => ({ ...prev, [rowId]: !!(changedData as any).sms }));
        }
        if (Object.prototype.hasOwnProperty.call(changedData, 'completed') || Object.prototype.hasOwnProperty.call(changedData, 'dispatchDone')) {
          const v = (changedData as any).completed ?? (changedData as any).dispatchDone;
          setDoneStatus(prev => ({ ...prev, [rowId]: !!v }));
        }
        
        Object.entries(changedData).forEach(([field, value]) => {
          const colIndex = fieldMap[field as keyof typeof fieldMap];
          if (colIndex !== undefined) {
            newData[rowIndex][colIndex] = value;
          }
        });
        
        // updatedAt 필드 업데이트
        if (timestamp) {
          newData[rowIndex][IDX.updatedAt] = new Date(timestamp).toISOString();
        }
      } else {
        // 행을 찾을 수 없으면 전체 재로드
        console.log('행을 찾을 수 없음. 전체 재로드 실행');
        debouncedFetch();
        return prev;
      }
      
      return newData;
    });
  }, [debouncedFetch]);

  // 안전한 부분 업데이트 여부 확인
  const isSafeForPartialUpdate = useCallback((data: any) => {
    return (
      data.rowId && 
      data.changedData &&
      Object.keys(data.changedData).length > 0 &&
      (!data.operationType || data.operationType === 'cell_edit')
    );
  }, []);

  // 소켓 이벤트: 부분 업데이트 우선, 안전하지 않으면 전체 재로드
  useEffect(() => {
    const myUserId = auth.currentUser?.uid;
    const handleReportUpdated = (data: { 
      id?: string, 
      date: string, 
      eventId?: string, 
      userId?: string,
      rowId?: string,
      changedData?: any,
      operationType?: string,
      timestamp?: string,
      source?: string,
    }) => {
      if (data.source === 'settlement') return;
      if (data.userId && data.userId === myUserId) return;
      // 선택된 날짜가 여러 개인 경우에도 대응
      const selectedDates = (selectedDate || '')
        .split(',')
        .map(d => d.trim())
        .filter(Boolean);
      if (!selectedDates.includes(data.date)) return;
      if (data.eventId && lastEventIdRef.current === data.eventId) return;
      lastEventIdRef.current = data.eventId || null;
      
      // 편집 중인 셀이 있으면 업데이트 지연
      if (editingCellsRef.current.size > 0) {
        setTimeout(() => {
          if (editingCellsRef.current.size === 0) {
            // 안전한 부분 업데이트 가능한 경우
            if (isSafeForPartialUpdate(data)) {
              updateSingleRow(data.rowId!, data.changedData!, data.timestamp ? new Date(data.timestamp).getTime() : undefined);
            } else {
              // 위험한 경우 전체 재로드
              debouncedFetch();
            }
          }
        }, 1000);
        return;
      }
      
      // 안전한 부분 업데이트 가능한 경우
      if (isSafeForPartialUpdate(data)) {
        updateSingleRow(data.rowId!, data.changedData!, data.timestamp ? new Date(data.timestamp).getTime() : undefined);
      } else {
        // 위험한 경우 또는 부분 업데이트 불가능한 경우 전체 재로드
        setLoading(true);
        debouncedFetch();
      }
    };
    socket.on('report-updated', handleReportUpdated);
    return () => {
      socket.off('report-updated', handleReportUpdated);
    };
  }, [selectedDate, debouncedFetch, updateSingleRow, isSafeForPartialUpdate]);

  // 구버전 서버(rowsUpdated) 이벤트도 호환 처리
  useEffect(() => {
    const myUserId = auth.currentUser?.uid;
    const handleRowsUpdated = (data: { 
      id?: string, 
      date: string, 
      eventId?: string, 
      userId?: string,
      rowId?: string,
      changedData?: any,
      operationType?: string,
      timestamp?: string,
      source?: string,
    }) => {
      if (data.source === 'settlement') return;
      if (data.userId && data.userId === myUserId) return;
      const selectedDates = (selectedDate || '')
        .split(',')
        .map(d => d.trim())
        .filter(Boolean);
      if (!selectedDates.includes(data.date)) return;
      if (data.eventId && lastEventIdRef.current === data.eventId) return;
      lastEventIdRef.current = data.eventId || null;
      if (editingCellsRef.current.size > 0) {
        setTimeout(() => {
          if (editingCellsRef.current.size === 0) {
            if (isSafeForPartialUpdate(data)) {
              updateSingleRow(data.rowId!, data.changedData!, data.timestamp ? new Date(data.timestamp).getTime() : undefined);
            } else {
              debouncedFetch();
            }
          }
        }, 1000);
        return;
      }
      if (isSafeForPartialUpdate(data)) {
        updateSingleRow(data.rowId!, data.changedData!, data.timestamp ? new Date(data.timestamp).getTime() : undefined);
      } else {
        setLoading(true);
        debouncedFetch();
      }
    };
    socket.on('rowsUpdated', handleRowsUpdated);
    return () => {
      socket.off('rowsUpdated', handleRowsUpdated);
    };
  }, [selectedDate, debouncedFetch, updateSingleRow, isSafeForPartialUpdate]);

  // 행 추가/삭제 등 구조 변경 시 전체 재로드 (멀티 날짜 지원)
  useEffect(() => {
    const handleStructureUpdated = (data: { date: string }) => {
      const selectedDates = (selectedDate || '')
        .split(',')
        .map(d => d.trim())
        .filter(Boolean);
      if (!selectedDates.includes(data.date)) return;
      setLoading(true);
      debouncedFetch();
    };
    socket.on('dispatch-row-structure-updated', handleStructureUpdated);
    return () => {
      socket.off('dispatch-row-structure-updated', handleStructureUpdated);
    };
  }, [selectedDate, debouncedFetch]);

  // 4) 포커스/가시성/소켓 재연결 시 강제 재조회 (현재 구조/기능 변경 없이 보완)
  useEffect(() => {
    const forceRefetch = () => {
      if (!selectedDate) return;
      setLoading(true);
      debouncedFetch();
    };
    const handleFocus = () => forceRefetch();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') forceRefetch();
    };
    const handleSocketConnect = () => forceRefetch();
    const handleSocketReconnect = () => forceRefetch();
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    socket.on('connect', handleSocketConnect);
    socket.on('reconnect', handleSocketReconnect as any);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      socket.off('connect', handleSocketConnect);
      socket.off('reconnect', handleSocketReconnect as any);
    };
  }, [selectedDate, debouncedFetch]);

  // 배차오더/미배차 카운트 계산 함수 (운송구간에 '창고료' 있으면 배차완료로 간주)
  const calculateOrderCounts = useCallback((data: any[][]) => {
    let orderCount = 0;
    let notAssignedCount = 0;
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const 운송구간 = row[IDX.route] != null ? String(row[IDX.route]).trim() : '';
      const 차량번호 = row[IDX.vehicle] && String(row[IDX.vehicle]).trim();
      const 창고료 = 운송구간.includes('창고료');
      if (운송구간) {
        orderCount++;
        if (!차량번호 && !창고료) notAssignedCount++;
      }
    }
    setOrderCount(orderCount);
    setNotAssignedCount(notAssignedCount);
  }, []);

  // tableDataState가 바뀔 때마다 배차오더/미배차 카운트 계산
  useEffect(() => {
    calculateOrderCounts(tableDataState);
  }, [tableDataState, calculateOrderCounts]);

  const [routeAutoList, setRouteAutoList] = useState<any[]>([]);
  const [routeAutoRow, setRouteAutoRow] = useState<number | null>(null);
  const [routeAutoCol, setRouteAutoCol] = useState<number | null>(null);
  const [routeAutoBoxPos, setRouteAutoBoxPos] = useState<{left: number, top: number} | null>(null);

  // 운임 자동입력 상태 추가
  const [rateAutoList, setRateAutoList] = useState<any[]>([]);
  const [rateAutoRow, setRateAutoRow] = useState<number | null>(null);
  const [rateAutoCol, setRateAutoCol] = useState<number | null>(null);
  const [rateAutoBoxPos, setRateAutoBoxPos] = useState<{left: number, top: number} | null>(null);

  const [routeMemoHover, setRouteMemoHover] = useState<{ row: number; left: number; top: number; text: string } | null>(null);
  const [routeMemoEdit, setRouteMemoEdit] = useState<{ row: number; rowId: string; text: string } | null>(null);
  const [routeMemoSaving, setRouteMemoSaving] = useState(false);
  const [routeMemoCopySnackbar, setRouteMemoCopySnackbar] = useState(false);
  const routeMemoHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const routeMemoSuppressHoverRef = useRef(false);
  const routeMemoInputRef = useRef<HTMLTextAreaElement | null>(null);

  const releaseHandsontableFocus = useCallback(() => {
    const hot = hotRef.current?.hotInstance as Handsontable | undefined;
    if (!hot) return;
    const editor = hot.getActiveEditor?.();
    if (editor && typeof editor.finishEditing === 'function') {
      try {
        if (typeof editor.isOpened === 'function' && editor.isOpened()) {
          editor.finishEditing(false, false);
        }
      } catch {
        // 편집 중이 아니면 무시
      }
    }
    if (typeof (hot as any).deselectCell === 'function') {
      (hot as any).deselectCell();
    }
    const textarea = hot.rootElement?.querySelector('textarea.handsontableInput');
    if (textarea instanceof HTMLElement) {
      textarea.blur();
    }
  }, []);

  const focusRouteMemoInput = useCallback(() => {
    releaseHandsontableFocus();
    window.setTimeout(() => {
      const el = routeMemoInputRef.current;
      if (!el) return;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }, 0);
  }, [releaseHandsontableFocus]);

  const copyRouteMemoText = useCallback(async (text: string) => {
    const trimmed = String(text ?? '').trim();
    if (!trimmed) return;
    try {
      await navigator.clipboard.writeText(trimmed);
      setRouteMemoCopySnackbar(true);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = trimmed;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setRouteMemoCopySnackbar(true);
      } catch {
        window.alert('클립보드 복사에 실패했습니다.');
      }
    }
  }, []);

  const clearRouteMemoHoverTimers = () => {
    if (routeMemoHoverTimerRef.current) {
      clearTimeout(routeMemoHoverTimerRef.current);
      routeMemoHoverTimerRef.current = null;
    }
  };

  const dismissRouteMemoHover = useCallback(() => {
    clearRouteMemoHoverTimers();
    setRouteMemoHover(null);
  }, []);

  const suppressRouteMemoHover = useCallback(() => {
    routeMemoSuppressHoverRef.current = true;
    dismissRouteMemoHover();
  }, [dismissRouteMemoHover]);

  const resumeRouteMemoHover = useCallback(() => {
    routeMemoSuppressHoverRef.current = false;
  }, []);

  const saveRouteMemo = useCallback(async (rowIndex: number, routeMemo: string) => {
    const rowId = String(tableDataState[rowIndex]?.[IDX.id] ?? '').trim();
    const company = String(tableDataState[rowIndex]?.[IDX.company] ?? '').trim();
    const route = String(tableDataState[rowIndex]?.[IDX.route] ?? '').trim();
    if (!rowId || !selectedDate) return false;
    if (routeMemo.trim() !== '' && (!company || !route)) return false;
    try {
      const res = await fetch(`/api/dispatch-reports/${selectedDate}/rows/${rowId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ routeMemo, userId: auth.currentUser?.uid }),
      });
      if (!res.ok) throw new Error('저장 실패');
      setTableDataState((prev) => {
        const next = [...prev];
        const idx = next.findIndex((row) => row[IDX.id] === rowId);
        if (idx === -1) return prev;
        const row = [...next[idx]];
        row[IDX.routeMemo] = routeMemo;
        next[idx] = row;
        return next;
      });
      hotRef.current?.hotInstance?.render();
      return true;
    } catch {
      alert('운송구간 메모 저장에 실패했습니다.');
      return false;
    }
  }, [selectedDate, tableDataState]);

  const openRouteMemoEditor = useCallback((rowIndex: number) => {
    dismissRouteMemoHover();
    const rowId = String(tableDataState[rowIndex]?.[IDX.id] ?? '').trim();
    const company = String(tableDataState[rowIndex]?.[IDX.company] ?? '').trim();
    const route = String(tableDataState[rowIndex]?.[IDX.route] ?? '').trim();
    if (!rowId || !company || !route) return;
    const text = String(tableDataState[rowIndex]?.[IDX.routeMemo] ?? '');
    releaseHandsontableFocus();
    setRouteMemoEdit({ row: rowIndex, rowId, text });
  }, [tableDataState, dismissRouteMemoHover, releaseHandsontableFocus]);

  useEffect(() => {
    if (!routeMemoEdit) return;
    focusRouteMemoInput();
  }, [routeMemoEdit, focusRouteMemoInput]);

  useEffect(() => () => clearRouteMemoHoverTimers(), []);

  const handleAfterChange = async (changes: any, source: string) => {
    if (source !== 'edit') return;
    if (skipRouteAutoRef.current) {
      skipRouteAutoRef.current = false;
      return; // 자동완성 선택 후에는 자동완성 로직 실행하지 않음
    }
    for (const [row, col, oldValue, newValue] of changes) {
      // 편집 중인 셀 추적
      const cellKey = `${row}-${col}`;
      editingCellsRef.current.add(cellKey);
      
      // 기존 저장 로직은 그대로 유지 (실시간 동기화를 위해 개별 저장 유지)
      const rowId = tableDataState[row][IDX.id];
      const colName = COLUMN_HEADERS[col];
      const fieldName = COLUMN_FIELD_MAP[colName];
      
      // 차량번호 자동완성 로직 추가 (디바운싱 강화)
      if (col === IDX.vehicle && typeof newValue === 'string' && newValue.length >= 3) { // 2글자 → 3글자로 변경
        try {
          const res = await fetch(`/api/vehicles/search?query=${encodeURIComponent(newValue)}`);
          const vehicles = await res.json();
          
          if (vehicles.length > 0) {
            const ht = hotRef.current?.hotInstance;
            const td = ht?.getCell(row, col);
            if (td) {
              const rect = td.getBoundingClientRect();
              // 최소 1개 높이 보장
              const dropdownHeight = 44 * Math.max(vehicles.length, 1);
              const spaceBelow = window.innerHeight - (rect.top + rect.height);
              const spaceAbove = rect.top;
              let top = rect.top + rect.height + window.scrollY;
              if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                top = rect.top + window.scrollY - dropdownHeight;
              }
              setVehicleAutoBoxPos({
                left: rect.left + window.scrollX,
                top
              });
            }
            setVehicleAutoList(vehicles);
            setVehicleAutoRow(row);
            setVehicleAutoCol(col);
          } else {
            setVehicleAutoList([]);
          }
        } catch (error) {
          console.error('차량 검색 중 오류:', error);
          setVehicleAutoList([]);
        }
        // 차량번호가 바뀌면 사이드바 미배차 상태 즉시 갱신
        window.dispatchEvent(new Event('refresh-unassigned-dates'));
      } else if (col === IDX.vehicle) {
        setVehicleAutoList([]);
        // 차량번호가 바뀌면 사이드바 미배차 상태 즉시 갱신
        window.dispatchEvent(new Event('refresh-unassigned-dates'));
      }

      // 운송구간 자동완성 로직 추가 (2번 컬럼) - 디바운싱 강화
      if (col === IDX.route && typeof newValue === 'string' && newValue.length >= 3) { // 2글자 → 3글자로 변경
        try {
          const res = await fetch(`/api/dispatch-reports/route-stats-cache-unique?query=${encodeURIComponent(newValue)}`);
          const data = await res.json();
          const routes = (data.results || data).filter((r: any) => typeof r.route === 'string').slice(0, 8); // 10개 → 8개로 제한
          if (routes.length > 0) {
            const ht = hotRef.current?.hotInstance;
            const td = ht?.getCell(row, col);
            if (td) {
              const rect = td.getBoundingClientRect();
              const dropdownHeight = 44 * Math.min(routes.length, 10); // 후보 1개당 44px, 최대 10개
              const spaceBelow = window.innerHeight - (rect.top + rect.height);
              const spaceAbove = rect.top;
              let top = rect.top + rect.height + window.scrollY;
              if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                // 아래 공간이 부족하고 위에 충분하면 위로 띄움
                top = rect.top + window.scrollY - dropdownHeight;
              }
              setRouteAutoBoxPos({ left: rect.left + window.scrollX, top });
            }
            setRouteAutoList(routes as any[]);
            setRouteAutoRow(row);
            setRouteAutoCol(col);
          } else {
            setRouteAutoList([]);
          }
        } catch (error) {
          setRouteAutoList([]);
        }
        // 운송구간이 바뀌면 사이드바 미배차 상태 즉시 갱신
        window.dispatchEvent(new Event('refresh-unassigned-dates'));
      } else if (col === IDX.route) {
        setRouteAutoList([]);
        // 운송구간이 바뀌면 사이드바 미배차 상태 즉시 갱신
        window.dispatchEvent(new Event('refresh-unassigned-dates'));
      }

      // 운임 자동입력: 운송구간(2), 차종(4), 거래처(0) 모두 입력 시
      if ([IDX.company, IDX.route, IDX.cargo].includes(col)) {
        const company = tableDataState[row][IDX.company]?.trim();
        const route = tableDataState[row][IDX.route]?.trim();
        const cargo = tableDataState[row][IDX.cargo]?.trim();
        if (company && route && cargo) {
          try {
            const res = await fetch('/api/trucking-rate/search', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ company, route, cargo })
            });
            const rates = await res.json();
            if (Array.isArray(rates) && rates.length > 0) {
              if (rates.length === 1) {
                // 1개면 바로 청구운송비 입력
                if (hotRef.current) {
                  hotRef.current.hotInstance.setDataAtCell(row, 5, rates[0].billingAmount, 'edit');
                }
              } else {
                // 여러 개면 fuelSegment별 드롭다운
                const ht = hotRef.current?.hotInstance;
                const td = ht?.getCell(row, IDX.billing);
                if (td) {
                  const rect = td.getBoundingClientRect();
                  const dropdownHeight = 44 * Math.min(rates.length, 10);
                  const spaceBelow = window.innerHeight - (rect.top + rect.height);
                  const spaceAbove = rect.top;
                  let top = rect.top + rect.height + window.scrollY;
                  if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                    top = rect.top + window.scrollY - dropdownHeight;
                  }
                  setRateAutoBoxPos({ left: rect.left + window.scrollX, top });
                }
                setRateAutoList(rates);
                setRateAutoRow(row);
                setRateAutoCol(IDX.billing);
              }
            } else {
              setRateAutoList([]);
              setRateAutoRow(null);
              setRateAutoCol(null);
              setRateAutoBoxPos(null);
            }
          } catch (e) {
            setRateAutoList([]);
            setRateAutoRow(null);
            setRateAutoCol(null);
            setRateAutoBoxPos(null);
          }
        }
      }

      // 연락처 자동 하이픈 변환
      let updateBody: any = { [fieldName]: newValue };
      if (col === IDX.contact && typeof newValue === 'string') {
        const formatted = formatPhoneNumber(newValue);
        if (formatted !== newValue && hotRef.current) {
          hotRef.current.hotInstance.setDataAtCell(row, col, formatted);
          updateBody[fieldName] = formatted;
        }
      }

      // 정산상태가 '미정산'으로 바뀌면 정산일도 ''로
      if (fieldName === 'settlementStatus' && newValue === '미정산') {
        updateBody.settlementDate = '';
        // UI도 즉시 비움
        const settlementDateColIdx = 14; // 정산일 컬럼 인덱스
        if (hotRef.current) {
          hotRef.current.hotInstance.setDataAtCell(row, settlementDateColIdx, '');
          hotRef.current.hotInstance.render();
        }
      }

      // profit 자동 계산 및 저장
      if (fieldName === 'billingAmount' || fieldName === 'paymentAmount') {
        const billingAmount = fieldName === 'billingAmount'
          ? safeAmountToNumber(newValue)
          : safeAmountToNumber(tableDataState[row][IDX.billing]);
        const paymentAmount = fieldName === 'paymentAmount'
          ? safeAmountToNumber(newValue)
          : safeAmountToNumber(tableDataState[row][IDX.payment]);
        updateBody.profit = billingAmount - paymentAmount;
      }

      // 기존 저장 로직 계속 실행
      if (!rowId || !fieldName) continue;
      
      // 화주전달 메시지 저장 시 디버깅 로그
      if (fieldName === 'customerMessage') {
        // console.log('[DispatchReportPage] 화주전달 메시지 저장 시작:', {
        //   rowId,
        //   fieldName,
        //   oldValue,
        //   newValue,
        //   updateBody
        // });
      }
      
      try {
        const res = await fetch(`/api/dispatch-reports/${selectedDate}/rows/${rowId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...updateBody, userId: auth.currentUser?.uid })
        });
        if (!res.ok) {
          if (res.status === 404) {
            const err = await res.json();
            alert(err.error || '해당 행이 존재하지 않습니다.');
            if (hotRef.current) {
              hotRef.current.hotInstance.setDataAtCell(row, col, oldValue);
            }
            continue;
          }
          if (hotRef.current) {
            hotRef.current.hotInstance.setDataAtCell(row, col, oldValue);
          }
          throw new Error(`저장 실패: ${res.status}`);
        }
        
        // 화주전달 메시지 저장 성공 시 디버깅 로그
        if (fieldName === 'customerMessage') {
          // console.log('[DispatchReportPage] 화주전달 메시지 저장 성공:', {
          //   rowId,
          //   newValue,
          //   timestamp: new Date().toISOString()
          // });
        }
        
        // 저장 성공 시 편집 중인 셀 추적에서 제거
        editingCellsRef.current.delete(cellKey);
        
        // 저장 성공 시 배차오더/미배차 카운트 갱신
        calculateOrderCounts(tableDataState);
      } catch (error) {
        console.error('[DispatchReportPage] 저장 실패:', {
          rowId,
          fieldName,
          error: error instanceof Error ? error.message : error
        });
        
        // 화주전달 메시지 저장 실패 시 특별 처리
        if (fieldName === 'customerMessage') {
          console.error('[DispatchReportPage] 화주전달 메시지 저장 실패 - 원래 값으로 복원:', {
            rowId,
            oldValue,
            newValue
          });
        }
        
        if (hotRef.current) {
          hotRef.current.hotInstance.setDataAtCell(row, col, oldValue);
        }
        
        // 저장 실패 시 편집 중인 셀 추적에서 제거
        editingCellsRef.current.delete(cellKey);
        
        // 사용자에게 에러 알림
        if (fieldName === 'customerMessage') {
          alert('화주전달 메시지 저장에 실패했습니다. 다시 시도해주세요.');
        }
      }
    }
  };

  const handleSelectVehicleAuto = async (vehicle: any) => {
    if (vehicleAutoRow == null || !selectedDate) return;
    
    const rowId = tableDataState[vehicleAutoRow][IDX.id];
    if (!rowId) return;

    try {
      const res = await fetch(`/api/dispatch-reports/${selectedDate}/rows/${rowId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vehicleNumber: vehicle.vehicleNumber,
          contact: vehicle.contact,
          driverName: vehicle.driverName,
          businessNumber: vehicle.businessNumber
        })
      });
      
      if (!res.ok) throw new Error('저장 실패');
      
      // 성공 시 자동완성 UI 닫기
      setVehicleAutoList([]);
      setVehicleAutoRow(null);
      setVehicleAutoCol(null);
      setVehicleAutoBoxPos(null);
      
      // 데이터 새로고침
      debouncedFetch();
    } catch (error) {
      console.error('차량 정보 저장 중 오류:', error);
    }
  };

  const openDispatchSmsSentList = useCallback(async () => {
    if (!smsRowId) return;
    const row = tableDataState.find((r) => r[IDX.id] === smsRowId);
    const phone = row ? String(row[IDX.contact] || '').trim() : String(smsPhone || '').trim();
    const vehicle = row ? String(row[IDX.vehicle] || '').trim() : '';
    const digits = phone.replace(/[^0-9]/g, '');
    setSmsSentListSubtitle(`${vehicle ? `차량 ${vehicle} · ` : ''}${formatPhoneNumber(digits)}`);
    setSmsSentListOpen(true);
    setSmsSentListLoading(true);
    setSmsSentListError(null);
    setSmsSentListItems([]);
    try {
      const qs = new URLSearchParams();
      if (phone) qs.set('phone', phone);
      if (vehicle) qs.set('vehicleNumber', vehicle);
      const res = await fetch(`/api/sms/history/dispatch?${qs.toString()}`);
      const text = await res.text();
      let data: any = {};
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error('서버에서 목록을 불러오지 못했습니다. (응답 형식 오류)');
      }
      if (!res.ok) throw new Error(data?.error || '조회 실패');
      setSmsSentListItems(Array.isArray(data.history) ? data.history : []);
    } catch (err: any) {
      setSmsSentListError(err?.message || '목록을 불러오지 못했습니다.');
    } finally {
      setSmsSentListLoading(false);
    }
  }, [smsRowId, tableDataState, smsPhone]);

  // 문자 셀 커스텀 렌더러 (UI 전용)
  const smsRenderer = (
    instance: any,
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: any,
    value: any,
    cellProperties: any
  ) => {
    Handsontable.dom.empty(td);
    const contact = tableDataState[row]?.[IDX.contact];
    const rowId = tableDataState[row]?.[IDX.id] as string;
    const sent = rowId ? !!smsStatus[rowId] : false;
    if (!contact || String(contact).trim() === '') {
      return td;
    }
    const btn = document.createElement('button');
    btn.textContent = sent ? '재전송' : '전송';
    btn.style.background = sent ? '#bdbdbd' : '#1976d2';
    btn.style.color = '#fff';
    btn.style.border = 'none';
    btn.style.borderRadius = '4px';
    btn.style.padding = '2px 8px';
    btn.style.fontSize = '12px';
    btn.style.cursor = 'pointer';
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const phone = String(contact || '');
      setSmsRowId(rowId || '');
      setSmsPhone(phone);
      setSmsDefaultMsg('');
      // 회사 템플릿 미리 로드하여 전역에 저장 (간단 캐시)
      try {
        const company = (tableDataState[row] && tableDataState[row][IDX.company]) ? String(tableDataState[row][IDX.company]).trim() : '';
        if (company) {
          const res = await fetch(`/api/sms/templates?companyId=${encodeURIComponent(company)}`);
          const text = await res.text();
          let data: any = {}; try { data = JSON.parse(text); } catch { data = {}; }
          const list = Array.isArray(data.templates) ? data.templates.map((t:any)=>({ id: t.id, name: t.name, content: t.content, media: t.media })) : [];
          (window as any).__dispatchCompanyTemplates = (window as any).__dispatchCompanyTemplates || {};
          (window as any).__dispatchCompanyTemplates[company] = list;
        }
      } catch {}
      setSmsOpen(true);
    });
    td.style.textAlign = 'center';
    td.appendChild(btn);
    return td;
  };

  const routeRenderer = useCallback((
    instance: any,
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: any,
    value: any,
    cellProperties: any
  ) => {
    Handsontable.renderers.TextRenderer(instance, td, row, col, prop, value, cellProperties);
    const routeMemo = String(tableDataState[row]?.[IDX.routeMemo] ?? '').trim();
    td.style.position = 'relative';
    td.style.overflow = 'hidden';
    td.style.textOverflow = 'ellipsis';
    td.style.whiteSpace = 'nowrap';

    const existingIndicator = td.querySelector('.route-memo-indicator');
    if (existingIndicator) existingIndicator.remove();

    if (routeMemo) {
      td.classList.add('has-route-memo');
      const indicator = document.createElement('span');
      indicator.className = 'route-memo-indicator';
      indicator.setAttribute('aria-label', '운송구간 메모 있음');
      td.appendChild(indicator);

      td.onmouseenter = () => {
        if (routeMemoSuppressHoverRef.current) return;
        clearRouteMemoHoverTimers();
        routeMemoHoverTimerRef.current = setTimeout(() => {
          if (routeMemoSuppressHoverRef.current) return;
          const rect = td.getBoundingClientRect();
          const pos = computeRouteMemoHoverPosition(rect, routeMemo);
          setRouteMemoHover({
            row,
            left: pos.left,
            top: pos.top,
            text: routeMemo,
          });
        }, 200);
      };
      td.onmouseleave = () => {
        if (routeMemoHoverTimerRef.current) {
          clearTimeout(routeMemoHoverTimerRef.current);
          routeMemoHoverTimerRef.current = null;
        }
      };
      td.onmousedown = (e: MouseEvent) => {
        if (e.button === 2) suppressRouteMemoHover();
      };
      td.oncontextmenu = () => {
        suppressRouteMemoHover();
      };
    } else {
      td.classList.remove('has-route-memo');
      td.onmouseenter = null;
      td.onmouseleave = null;
      td.onmousedown = null;
      td.oncontextmenu = null;
    }
    return td;
  }, [tableDataState, suppressRouteMemoHover]);

  const cells = (row: number, col: number) => {
    if (!tableDataState[row]) return {};
    const rowId = tableDataState[row][IDX.id];
    const cellProps: any = {};
    const company = (tableDataState[row][0] || '').trim();
    if (!company) return {};

    // 완료 행 여부
    const isDone = rowId ? !!doneStatus[rowId] : false;

    // 문자 컬럼: UI 전용 버튼 렌더러, 읽기 전용
    if (col === IDX.sms) {
      cellProps.readOnly = true;
      cellProps.renderer = smsRenderer;
      cellProps.className = (cellProps.className || '') + ' htCenter htMiddle';
      return cellProps;
    }

    // 연락처 컬럼은 줄바꿈 방지 (굵기/스타일 변화로 줄바꿈되는 현상 방지)
    if (col === IDX.contact) {
      cellProps.style = { ...cellProps.style, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
    }

    if (col === IDX.route) {
      cellProps.renderer = routeRenderer;
    }

    // 거래처상호 최상단 행 여부 확인
    let isFirstOccurrence = true;
    for (let i = 0; i < row; i++) {
      if ((tableDataState[i][0] || '').trim() === company) {
        isFirstOccurrence = false;
        break;
      }
    }

    // 메모 컬럼(14번째 컬럼)은 노란색 배경, 검은색 글씨, 일반
    if (col === IDX.memo) {
      cellProps.className = 'htMemoColumn htCenter htMiddle';
      cellProps.style = { backgroundColor: '#fff9c4', color: '#000000', fontWeight: 400 };
    }

    // 화주전달 컬럼(마지막 컬럼)은 진한 회색
    if (col === COLUMN_HEADERS.length - 1) {
      cellProps.className = 'htCustomerMessage htCenter htMiddle';
      cellProps.style = { backgroundColor: '#f5f5f5', color: '#424242', fontWeight: 700 };
    }

    // 거래처상호 컬럼(0번째 컬럼)은 굵은 글씨
    if (col === IDX.company) {
      cellProps.className = (cellProps.className || '') + ' company-name-bold';
      cellProps.style = { ...cellProps.style, fontWeight: 700 };
    }

    // 거래처상호 최상단 행 스타일 적용 (메모, 화주전달 컬럼 제외)
    if (isFirstOccurrence) {
      // 메모와 화주전달 컬럼은 파란색 배경 적용하지 않음
      if (col !== IDX.memo && col !== COLUMN_HEADERS.length - 1) {
        if (col === IDX.route) {
          cellProps.className = (cellProps.className || '') + ' htFirstCompany htLeft htMiddle';
          cellProps.style = { ...cellProps.style, backgroundColor: '#e3f2fd' };
        } else {
          cellProps.className = (cellProps.className || '') + ' htFirstCompany htCenter htMiddle';
          cellProps.style = { ...cellProps.style, backgroundColor: '#e3f2fd' };
        }
      }
      // 거래처 구분선 클래스 추가 (모든 컬럼에 적용)
      cellProps.className = (cellProps.className || '') + ' company-divider';
    }
    // 배차완료(주황색) 스타일 - 모든 컬럼에 우선 적용
    if (isDone) {
      cellProps.className = (cellProps.className || '') + ' dispatch-done';
      // 파란 배경보다 우선하도록 직접 background 지정
      cellProps.style = { ...cellProps.style, backgroundColor: '#ffe0b2' };
    }
    // 운송구간이 있는데 차량번호가 없는 행: 깜빡임만 적용 (단, blinkOffRows에 있으면 제외)
    // 비고, 메모, 화주전달(마지막) 컬럼은 깜빡이지 않음
    if (
      (tableDataState[row][IDX.route] && String(tableDataState[row][IDX.route]).trim() !== '') &&
      (!tableDataState[row][IDX.vehicle] || String(tableDataState[row][IDX.vehicle]).trim() === '') &&
      rowId && !blinkOffRows.has(rowId) && !isDone &&
      col !== IDX.sms && col !== IDX.remarks && col !== IDX.memo && col !== COLUMN_HEADERS.length - 1
    ) {
      cellProps.className = (cellProps.className || '') + (col === IDX.route ? ' blink-row htLeft htMiddle' : ' blink-row htCenter htMiddle');
    }
    // 기본 스타일 적용
    cellProps.className = (cellProps.className || '') + (col === IDX.route ? ' htLeft htMiddle' : ' htCenter htMiddle');
    // 차량 끝 4자리 검색 일치 행
    if (vehicleHighlightRow !== null && row === vehicleHighlightRow) {
      cellProps.className =
        (cellProps.className || '') +
        (col === IDX.vehicle ? ' dispatch-vehicle-search-hit-vehicle' : ' dispatch-vehicle-search-hit');
    }
    return cellProps;
  };

  useEffect(() => {
    // 0행(제목 컬럼) 스타일
    if (!document.getElementById('header-row-style')) {
      const style = document.createElement('style');
      style.id = 'header-row-style';
      style.innerHTML = `.custom-header-row { background: #f5f5f5 !important; font-weight: bold !important; }`;
      document.head.appendChild(style);
    }
    // 최상단 거래처 스타일
    if (!document.getElementById('first-company-style')) {
      const style = document.createElement('style');
      style.id = 'first-company-style';
      style.innerHTML = `.htFirstCompany { background: #e3f2fd !important; }`;
      document.head.appendChild(style);
    }
    // 운송구간+차량번호 없는 행 깜빡임 스타일
    if (!document.getElementById('blink-row-style')) {
      const style = document.createElement('style');
      style.id = 'blink-row-style';
      style.innerHTML = `
        @keyframes blink { 
          0%,100%{opacity:1; color:#111; font-weight:bold;} 
          50%{opacity:0.1; color:#111; font-weight:bold;} 
        } 
        .blink-row { 
          animation: blink 4.7s infinite; 
          background: #fff !important; 
          color: #111 !important; 
          font-weight: bold !important; 
        }
        /* 파란색 배경과 깜빡임을 동시에 적용 */
        .htFirstCompany.blink-row {
          background: #e3f2fd !important;
          animation: blink 4.7s infinite;
          color: #111 !important;
          font-weight: bold !important;
        }
      `;
      document.head.appendChild(style);
    }
    // 거래처상호 굵은 글씨 스타일
    if (!document.getElementById('company-name-bold-style')) {
      const style = document.createElement('style');
      style.id = 'company-name-bold-style';
      style.innerHTML = `
        .company-name-bold { font-weight: 700 !important; }
        .htFirstCompany.company-name-bold { font-weight: 700 !important; }
        .company-divider.company-name-bold { font-weight: 700 !important; }
      `;
      document.head.appendChild(style);
    }
    // 메모 컬럼 전용 스타일 (노란색, 검은색, 일반)
    if (!document.getElementById('memo-column-style')) {
      const style = document.createElement('style');
      style.id = 'memo-column-style';
      style.innerHTML = `.htMemoColumn { background: #fff9c4 !important; color: #000000 !important; font-weight: 400 !important; }`;
      document.head.appendChild(style);
    }

    // 거래처 구분선 스타일 (연한 구분선 - 위쪽만 적용)
    if (!document.getElementById('company-divider-style')) {
      const style = document.createElement('style');
      style.id = 'company-divider-style';
      style.innerHTML = `
        .company-divider {
          border-top: 2px solid #90caf9 !important;
          position: relative !important;
        }
        .company-divider::before {
          content: '' !important;
          position: absolute !important;
          top: -2px !important;
          left: -1px !important;
          right: -1px !important;
          height: 2px !important;
          background: linear-gradient(90deg, #90caf9 0%, #bbdefb 50%, #90caf9 100%) !important;
          border-radius: 1px !important;
          z-index: 999 !important;
        }
        /* 메모와 화주전달 컬럼에서도 구분선이 보이도록 강제 적용 */
        .htMemoColumn.company-divider::before,
        .htCustomerMessage.company-divider::before {
          z-index: 9999 !important;
        }
      `;
      document.head.appendChild(style);
    }
    // 화주전달 컬럼 전용 스타일 (진한 회색, 굵게)
    if (!document.getElementById('customer-message-style')) {
      const style = document.createElement('style');
      style.id = 'customer-message-style';
      style.innerHTML = `.htCustomerMessage { background: #f5f5f5 !important; color: #424242 !important; font-weight: 700 !important; }`;
      document.head.appendChild(style);
    }
  }, []);

  // 행 추가 함수 수정
  const handleAddRow = async (rowIndex: number) => {
    if (!selectedDate) return;
    const dates = selectedDate.split(',').map(d => d.trim()).filter(Boolean);
    if (dates.length === 0) return;
    const targetDate = dates[0]; // 항상 첫 번째 날짜를 기준으로 함
    
    // 회사명 추출
    let company = '';
    if (tableDataState[rowIndex]) {
      company = tableDataState[rowIndex][IDX.company] || '';
    }
    // 같은 회사 그룹 내에서 마지막 order 찾기
    const companyRows = tableDataState.filter(r => (r[IDX.company] || '') === company);
    // order 인덱스도 한 칸 뒤로
    const lastOrder = companyRows.length > 0
      ? Math.max(...companyRows.map((r: any[]) => r[IDX.order] || 0))
      : 0;
    const order = lastOrder + 1000;
    // 첫 번째 선택된 날짜에 행 추가
    const newRow = {
      date: targetDate, // 단일 날짜로 수정
      company,
      vehicleNumber: '', route: '', itemName: '', cargo: '', billingAmount: '', department: '', contact: '', paymentAmount: '', businessNumber: '', driverName: '', distance: '', weight: '', remarks: '', memo: '', routeMemo: '', profit: '', settlementStatus: '미정산', settlementDate: null, Receipt: '미수취', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), order
    };
    await fetch(`/api/dispatch-reports/${targetDate}/rows`, { // 단일 날짜로 수정
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newRow)
    });
    debouncedFetch();
  };

  // 행 삭제 함수
  const handleDeleteRow = async (rowIndex: number) => {
    if (!selectedDate) return;
    const row = tableDataState[rowIndex];
    if (!row) return;
    const rowId = row[IDX.id];
    if (!rowId) return;
    await fetch(`/api/dispatch-reports/${selectedDate}/rows/${rowId}`, {
      method: 'DELETE'
    });
    debouncedFetch();
  };

  // 커스텀 컨텍스트 메뉴 정의
  const customContextMenu = {
    items: {
      add_row: {
        name: '행 추가',
        callback: function(key: string, selection: any, clickEvent: any) {
          if (!selectedDate) return;
          const dates = selectedDate.split(',').map(d => d.trim()).filter(Boolean);
          if (dates.length === 0) return;
          const targetDate = dates[0]; // 항상 첫 번째 날짜를 기준으로 함

          const rowIndex = selection[0].start.row;
          const company = tableDataState[rowIndex][IDX.company] || '';
          let lastRow = rowIndex;
          for (let i = rowIndex + 1; i < tableDataState.length; i++) {
            if ((tableDataState[i][0] || '') !== company) break;
            lastRow = i;
          }
          const baseOrder = tableDataState[lastRow][IDX.order] || 0;
          const order = baseOrder + 1;
          const newRow = {
            date: targetDate, // 단일 날짜로 수정
            company,
            vehicleNumber: '', route: '', itemName: '', cargo: '', billingAmount: '', department: '', contact: '', paymentAmount: '', businessNumber: '', driverName: '', distance: '', weight: '', remarks: '', memo: '', routeMemo: '', profit: '', settlementStatus: '미정산', settlementDate: null, Receipt: '미수취', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), order
          };
          fetch(`/api/dispatch-reports/${targetDate}/rows`, { // 단일 날짜로 수정
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newRow)
          }).then(() => debouncedFetch());
        }
      },
      separator_after_add: {
        name: '────────',
        separator: true
      },
      route_memo_edit: {
        name: function (this: Handsontable) {
          const sel = this.getSelectedLast();
          if (!sel) return '운송구간 메모';
          const row = sel[0];
          const data = this.getSourceDataAtRow(row) as any[];
          const memo = data?.[IDX.routeMemo];
          return memo && String(memo).trim() ? '운송구간 메모 편집' : '운송구간 메모 추가';
        },
        disabled: function (this: Handsontable) {
          const sel = this.getSelectedLast();
          if (!sel) return true;
          const data = this.getSourceDataAtRow(sel[0]) as any[];
          return !isRouteMemoEligibleRow(data);
        },
        callback: function (key: string, selection: any) {
          const rowIndex = selection[0].start.row;
          openRouteMemoEditor(rowIndex);
        },
      },
      route_memo_delete: {
        name: '운송구간 메모 삭제',
        disabled: function (this: Handsontable) {
          const sel = this.getSelectedLast();
          if (!sel) return true;
          const row = sel[0];
          const data = this.getSourceDataAtRow(row) as any[];
          const rowId = String(data?.[IDX.id] ?? '').trim();
          const memo = String(data?.[IDX.routeMemo] ?? '').trim();
          // 삭제는 운송구간이 비어 있어도 허용 (기존 메모 정리용)
          return !rowId || memo === '';
        },
        callback: function (key: string, selection: any) {
          const rowIndex = selection[0].start.row;
          if (!window.confirm('운송구간 메모를 삭제할까요?')) return;
          saveRouteMemo(rowIndex, '');
        },
      },
      separator_route_memo: {
        name: '────────',
        separator: true,
      },
      done_row: {
        name: '배차완료',
        callback: function(key: string, selection: any) {
          const rowIndex = selection[0].start.row;
          const row = tableDataState[rowIndex];
          if (!row) return;
          const rowId = row[IDX.id];
          if (!rowId || !selectedDate) return;
          fetch(`/api/dispatch-reports/${selectedDate}/rows/${rowId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ completed: true, userId: auth.currentUser?.uid })
          }).then(() => {
            setDoneStatus(prev => ({ ...prev, [rowId]: true }));
            if (hotRef.current?.hotInstance) hotRef.current.hotInstance.render();
          });
        }
      },
      undone_row: {
        name: '배차완료 해제',
        callback: function(key: string, selection: any) {
          const rowIndex = selection[0].start.row;
          const row = tableDataState[rowIndex];
          if (!row) return;
          const rowId = row[IDX.id];
          if (!rowId || !selectedDate) return;
          fetch(`/api/dispatch-reports/${selectedDate}/rows/${rowId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ completed: false, userId: auth.currentUser?.uid })
          }).then(() => {
            setDoneStatus(prev => ({ ...prev, [rowId]: false }));
            if (hotRef.current?.hotInstance) hotRef.current.hotInstance.render();
          });
        }
      },
      separator_before_delete: {
        name: '────────',
        separator: true
      },
      remove_row: {
        name: '삭제',
        callback: function(key: string, selection: any, clickEvent: any) {
          const rowIndex = selection[0].start.row;
          const row = tableDataState[rowIndex];
          if (!row) return;
          const rowId = row[IDX.id];
          if (!rowId) return;
          fetch(`/api/dispatch-reports/${selectedDate}/rows/${rowId}`, {
            method: 'DELETE'
          }).then(() => debouncedFetch());
        }
      }
    }
  };

  // summaryFieldMap: 입력란 이름 <-> DB 필드명 매핑
  const summaryFieldMap = {
    exportValue: 'exportramenNum',
    exportMemo: 'exportramenMemo',
    domesticValue: 'domesticramenNum',
    domesticMemo: 'domesticramenMemo',
    oilValue: 'oilramenNum',
    oilMemo: 'oilramenMemo',
  };

  // summary 입력란 이전 값 저장용 ref
  const prevSummaryValues = useRef({
    exportValue: '',
    exportMemo: '',
    domesticValue: '',
    domesticMemo: '',
    oilValue: '',
    oilMemo: '',
  });

  // summary 입력란 onBlur 시 DB 저장 (값이 바뀌었을 때만)
  const handleSummaryBlur = async (field: keyof typeof summaryFieldMap, value: string) => {
    if (!selectedDate) return;
    if (prevSummaryValues.current[field] === value) return; // 값이 안 바뀌었으면 저장 X
    prevSummaryValues.current[field] = value;
    // 입력값을 바로 setState로 반영
    switch (field) {
      case 'exportValue': setExportValue(value); break;
      case 'exportMemo': setExportMemo(value); break;
      case 'domesticValue': setDomesticValue(value); break;
      case 'domesticMemo': setDomesticMemo(value); break;
      case 'oilValue': setOilValue(value); break;
      case 'oilMemo': setOilMemo(value); break;
      default: break;
    }
    await fetch(`/api/dispatch-reports/${selectedDate}/summary`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [summaryFieldMap[field]]: value, userId: auth.currentUser?.uid })
    });
    // fetchSummary/로딩스피너 호출 X
  };

  // summary 데이터 fetch 함수
  const fetchSummary = useCallback(async () => {
    if (!selectedDate) return;
    setSummaryLoading(true);
    const res = await fetch(`/api/dispatch-reports/${selectedDate}/summary`);
    if (!res.ok) { setSummaryLoading(false); return; }
    const data = await res.json();
    setExportValue(data.exportramenNum || '');
    setExportMemo(data.exportramenMemo || '');
    setDomesticValue(data.domesticramenNum || '');
    setDomesticMemo(data.domesticramenMemo || '');
    setOilValue(data.oilramenNum || '');
    setOilMemo(data.oilramenMemo || '');
    setDoodle(data.doodle || '');
    setDoodle2(data.doodle2 || '');
    setDoodle3(data.doodle3 || '');
    // 수출/내수 데이터 로드
    setExportData(data.exportData || []);
    setDomesticData(data.domesticData || []);
    setSummaryLoading(false);
  }, [selectedDate]);

  // summary-updated 소켓 이벤트 리스너 추가
  useEffect(() => {
    const myUserId = auth.currentUser?.uid;
    const handleSummaryUpdated = (data: { date: string, userId?: string }) => {
      if (data.date !== selectedDate) return;
      if (data.userId && data.userId === myUserId) return; // 본인 입력이면 무시
      fetchSummary();
    };
    socket.on('summary-updated', handleSummaryUpdated);
    // 레거시 호환(summaryUpdated)
    socket.on('summaryUpdated', handleSummaryUpdated as any);
    return () => {
      socket.off('summary-updated', handleSummaryUpdated);
      socket.off('summaryUpdated', handleSummaryUpdated as any);
    };
  }, [selectedDate, fetchSummary]);

  // summary 데이터는 selectedDate가 바뀌거나 마운트 시 항상 fetchSummary로 로드
  useEffect(() => {
    fetchSummary();
  }, [selectedDate, fetchSummary]);

  // 차량번호 끝 4자리 → 일치 행 하이라이트
  useEffect(() => {
    const s = vehiclePlateSuffix.replace(/[^0-9]/g, '').slice(0, 4);
    if (s.length !== 4) {
      setVehicleHighlightRow(null);
      return;
    }
    setVehicleHighlightRow(findFirstRowByVehicleSuffix(tableDataState, s));
  }, [vehiclePlateSuffix, tableDataState]);

  useEffect(() => {
    const ht = hotRef.current?.hotInstance;
    if (!ht) return;
    const t = window.setTimeout(() => {
      try {
        if (vehicleHighlightRow != null) {
          ht.selectCell(vehicleHighlightRow, IDX.vehicle);
        }
        ht.render();
      } catch (e) {
        console.warn('vehicle search focus/render', e);
      }
    }, 80);
    return () => clearTimeout(t);
  }, [vehicleHighlightRow]);

  // 거래처 추가 버튼 클릭 시 Firestore에서 거래처 목록 fetch 및 모달 표시
  const handleAddCompanyClick = async () => {
    // Firestore에서 거래처 목록 불러오기 (컬렉션 구조)
    const colRef = collection(db, 'dispatch_company');
    const querySnap = await getDocs(colRef);
    const companies: string[] = [];
    querySnap.forEach(doc => {
      const data = doc.data();
      if (data.company && typeof data.company === 'string') {
        companies.push(data.company);
      }
    });
    // 이미 표에 있는 거래처상호는 제외 (중복 방지)
    // 주의: (주)아이엔티코리아 와 아이엔티코리아 처럼 실명 문자열이 다른 업체는 각각 별도로 취급해야 함.
    // 따라서 여기서는 normalizeCompanyName을 사용하지 않고, 원문(공백만 trim) 기준으로 비교한다.
    const usedCompanies = tableDataState
      .map((row: any[]) => (row[0] || '').toString().trim())
      .filter((v: string) => v !== '');
    const filtered = companies.filter(c => !usedCompanies.includes(c));
    setCompanyOptions(filtered);
    setShowCompanyModal(true);
  };

  // 거래처 선택 시 표 하단에 거래처상호 행만 추가
  const handleSelectCompany = async (company: string) => {
    if (!selectedDate) return;
    
    const dates = selectedDate.split(',').map(d => d.trim()).filter(Boolean);
    if (dates.length === 0) return;
    const targetDate = dates[0]; // 항상 첫 번째 날짜를 기준으로 함

    try {
      setShowCompanyModal(false);
      // 거래처명만 채운 새 행 데이터
      const newRowData = {
        date: targetDate, // 단일 날짜로 수정
        company,
        vehicleNumber: '',
        route: '',
        itemName: '',
        cargo: '',
        billingAmount: 0,
        department: '',
        contact: '',
        paymentAmount: 0,
        businessNumber: '',
        driverName: '',
        distance: 0,
        weight: 0,
        remarks: '',
        memo: '',
        routeMemo: '',
        profit: 0,
        settlementStatus: '미정산',
        settlementDate: null,
        Receipt: '미수취'
      };
      const res = await fetch(`/api/dispatch-reports/${targetDate}/rows`, { // 단일 날짜로 수정
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRowData)
      });
      if (!res.ok) throw new Error('거래처 추가에 실패했습니다.');
      debouncedFetch();
    } catch (error) {
      alert(error instanceof Error ? error.message : '거래처 추가 중 오류가 발생했습니다.');
    }
  };

  // 클립보드에서 "차량번호 이름 연락처" 형식 자동 분배 붙여넣기
  const handleAfterPaste = async (data: string[][], coords: any[]) => {
    setPasteError(null);
    
    try {
      const ht = hotRef.current?.hotInstance;
      if (!ht) {
        throw new Error('표가 초기화되지 않았습니다.');
      }

      // 1. "차량번호 이름 연락처" 형식 처리
      if (data.length === 1 && data[0].length === 1) {
        const pasted = data[0][0].trim();
        const parts = pasted.split(/[	\s,]+/); // 공백, 탭, 콤마 분리
        if (parts.length === 3) {
          const [vehicleNumber, driverName, contact] = parts;
          const sel = ht.getSelectedLast();
          if (sel) {
            const [row] = sel;
            // 각 셀 변경을 개별적으로 처리하여 handleAfterChange가 호출되도록 함
            await Promise.all([
              new Promise<void>((resolve) => {
                ht.setDataAtCell(row, 1, vehicleNumber, 'edit', () => resolve());
              }),
              new Promise<void>((resolve) => {
                ht.setDataAtCell(row, 10, driverName, 'edit', () => resolve());
              }),
              new Promise<void>((resolve) => {
                ht.setDataAtCell(row, 7, contact, 'edit', () => resolve());
              }),
              new Promise<void>((resolve) => {
                ht.setDataAtCell(row, 6, '24', 'edit', () => resolve()); // 소속(7번째 컬럼)에 24 자동입력
              })
            ]);
          }
          return false;
        }
      }
      
      // 2. 일반 붙여넣기 처리
      const sel = ht.getSelectedLast();
      if (!sel) {
        throw new Error('붙여넣을 위치를 선택해주세요.');
      }
      
      const [startRow, startCol] = sel;
      
      // 붙여넣은 데이터를 순회하며 각 셀 변경을 개별적으로 처리
      const promises: Promise<void>[] = [];
      
      data.forEach((rowData, rowOffset) => {
        rowData.forEach((cellData, colOffset) => {
          const row = startRow + rowOffset;
          const col = startCol + colOffset;
          if (row < ht.countRows() && col < ht.countCols()) {
            // 사업자번호 열 붙여넣기 시 하이픈 제거 (예: 264-22-00038 → 2642200038)
            let valueToSet = cellData;
            if (col === IDX.business && (typeof cellData === 'string' || typeof cellData === 'number')) {
              valueToSet = String(cellData).replace(/-/g, '');
            }
            promises.push(
              new Promise<void>((resolve) => {
                ht.setDataAtCell(row, col, valueToSet, 'edit', () => resolve());
              })
            );
          }
        });
      });
      
      await Promise.all(promises);
      return false;
    } catch (error) {
      setPasteError(error instanceof Error ? error.message : '붙여넣기 중 오류가 발생했습니다.');
      return false;
    }
  };

  // 낙서장 내용 저장 함수
  const handleScratchpadChange = async (value: string) => {
    if (!selectedDate) return;
    setScratchpadContent(value);
    try {
      await fetch(`/api/dispatch-reports/${selectedDate}/summary`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scratchpad: value })
      });
    } catch (error) {
      console.error('낙서장 저장 실패:', error);
    }
  };

  // 낙서 저장 함수
  const handleDoodleBlur = async (value: string) => {
    if (!selectedDate) return;
    setDoodle(value);
    await fetch(`/api/dispatch-reports/${selectedDate}/summary`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ doodle: value })
    });
  };

  const handleDoodle2Blur = async (value: string) => {
    if (!selectedDate) return;
    setDoodle2(value);
    await fetch(`/api/dispatch-reports/${selectedDate}/summary`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ doodle2: value })
    });
  };

  const handleDoodle3Blur = async (value: string) => {
    if (!selectedDate) return;
    setDoodle3(value);
    await fetch(`/api/dispatch-reports/${selectedDate}/summary`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ doodle3: value })
    });
  };

  // 수출/내수 데이터 저장 함수
  const handleSummaryDataSave = async (type: 'export' | 'domestic', data: ExportData[] | DomesticData[]) => {
    if (!selectedDate) return;
    
    const body: any = {};
    if (type === 'export') {
      body.exportData = data;
    } else {
      body.domesticData = data;
    }
    
    try {
      await fetch(`/api/dispatch-reports/${selectedDate}/summary`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, userId: auth.currentUser?.uid })
      });
    } catch (error) {
      console.error('수출/내수 데이터 저장 실패:', error);
    }
  };

  // 수출 데이터 변경 핸들러
  const handleExportDataChange = (index: number, field: keyof ExportData, value: string | number) => {
    const newData = [...exportData];
    newData[index] = { ...newData[index], [field]: value };
    setExportData(newData);
    handleSummaryDataSave('export', newData);
  };

  // 내수 데이터 변경 핸들러
  const handleDomesticDataChange = (index: number, field: keyof DomesticData, value: string | number) => {
    const newData = [...domesticData];
    newData[index] = { ...newData[index], [field]: value };
    setDomesticData(newData);
    handleSummaryDataSave('domestic', newData);
  };

  // 수출 행 추가
  const handleAddExportRow = () => {
    const newData = [...exportData, { export: 0, number: '' }];
    setExportData(newData);
    handleSummaryDataSave('export', newData);
  };

  // 내수 행 추가
  const handleAddDomesticRow = () => {
    const newData = [...domesticData, { domestic: 0, shipment: 0, number: '', dayNight: '' }];
    setDomesticData(newData);
    handleSummaryDataSave('domestic', newData);
  };

  // 수출 행 삭제
  const handleDeleteExportRow = (index: number) => {
    const newData = exportData.filter((_, i) => i !== index);
    setExportData(newData);
    handleSummaryDataSave('export', newData);
  };

  // 내수 행 삭제
  const handleDeleteDomesticRow = (index: number) => {
    const newData = domesticData.filter((_, i) => i !== index);
    setDomesticData(newData);
    handleSummaryDataSave('domestic', newData);
  };

  // 운송구간 input onChange 핸들러
  const handleRouteInputChange = async (rowIdx: number, value: string) => {
    setRouteInput(value);
    if (value.length >= 2) {
      // 유니크 운송구간 자동완성: routeStatsCacheUnique에서 route가 value로 시작하는 것만
      const res = await fetch(`/api/dispatch-reports/route-stats-cache-unique?query=${encodeURIComponent(value)}`);
      const data = await res.json();
      // 최대 10개만
      const routes = (data.results || data).filter((r: any) => typeof r.route === 'string').slice(0, 10);
      setRouteSuggestions(routes as any[]);
      setRouteDropdownRow(rowIdx);
      // 드롭다운 위치 계산 (input 위치)
      const inputEl = routeInputRefs.current[rowIdx];
      if (inputEl) {
        const rect = inputEl.getBoundingClientRect();
        setRouteDropdownPos({ left: rect.left + window.scrollX, top: rect.bottom + window.scrollY });
      }
    } else {
      setRouteSuggestions([]);
      setRouteDropdownRow(null);
      setRouteDropdownPos(null);
    }
  };

  // 운송구간 자동완성 드롭다운에서 선택 시
  const handleSelectRouteSuggestion = (rowIdx: number, routeObj: any) => {
    // 표 데이터에 반영 (route, distance만)
    if (hotRef.current) {
      hotRef.current.hotInstance.setDataAtCell(rowIdx, IDX.route, routeObj.route, 'edit');
      hotRef.current.hotInstance.setDataAtCell(rowIdx, IDX.distance, routeObj.distance, 'edit');
      // hotRef.current.hotInstance.setDataAtCell(rowIdx, 12, routeObj.weight, 'edit'); // 중량 자동입력 제거
    }
    setRouteSuggestions([]);
    setRouteDropdownRow(null);
    setRouteDropdownPos(null);
  };

  const handleSelectRouteAuto = async (routeObj: any) => {
    if (routeAutoRow == null || !selectedDate) return;

    const rowId = tableDataState[routeAutoRow][IDX.id];
    if (!rowId) return;

    // 서버에 저장 (route, distance만)
    try {
      const res = await fetch(`/api/dispatch-reports/${selectedDate}/rows/${rowId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ route: routeObj.route, distance: routeObj.distance })
      });
      if (!res.ok) throw new Error('저장 실패');
    } catch (error) {
      alert('운송구간 저장 실패');
    }

    skipRouteAutoRef.current = true; // 다음 afterChange는 무시
    if (hotRef.current) {
      hotRef.current.hotInstance.setDataAtCell(routeAutoRow, IDX.route, routeObj.route, 'edit');
      hotRef.current.hotInstance.setDataAtCell(routeAutoRow, IDX.distance, routeObj.distance, 'edit');
      // hotRef.current.hotInstance.setDataAtCell(routeAutoRow, 12, routeObj.weight, 'edit'); // 중량 자동입력 제거
    }
    setRouteAutoList([]);
    setRouteAutoRow(null);
    setRouteAutoCol(null);
    setRouteAutoBoxPos(null);
  };

  // 구간별 색상 매핑 함수 (오렌지 계열, 점점 진해짐)
  const getSegmentColor = (segment: string) => {
    if (!segment) return { background: '#fff3e0', color: '#222' };
    const seg = String(segment).replace(/[^0-9]/g, '');
    switch (seg) {
      case '1': return { background: '#fff3e0', color: '#f57c00' }; // 연한 오렌지
      case '2': return { background: '#ffe0b2', color: '#f57c00' };
      case '3': return { background: '#ffcc80', color: '#ef6c00' };
      case '4': return { background: '#ffb74d', color: '#e65100' };
      case '5': return { background: '#ffa726', color: '#e65100' }; // 진한 오렌지
      default: return { background: '#ff9800', color: '#fff' }; // 6구간 이상
    }
  };

  // 운임 드롭다운에서 선택 시
  const handleSelectRateAuto = (rate: any) => {
    if (rateAutoRow == null) return;
    if (hotRef.current) {
      hotRef.current.hotInstance.setDataAtCell(rateAutoRow, 5, rate.billingAmount, 'edit');
    }
    setRateAutoList([]);
    setRateAutoRow(null);
    setRateAutoCol(null);
    setRateAutoBoxPos(null);
  };

  // 운임 드롭다운 ESC 닫기 리스너
  useEffect(() => {
    if (!(rateAutoList.length > 0 && rateAutoRow != null && rateAutoCol === 5 && rateAutoBoxPos)) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setRateAutoList([]);
        setRateAutoRow(null);
        setRateAutoCol(null);
        setRateAutoBoxPos(null);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [rateAutoList, rateAutoRow, rateAutoCol, rateAutoBoxPos]);

  // 브라우저 줌 레벨 변경 감지 및 테이블 리렌더링
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;
    let zoomTimeout: NodeJS.Timeout;
    
    const renderTable = () => {
      if (hotRef.current?.hotInstance) {
        hotRef.current.hotInstance.render();
      }
    };
    
    // 윈도우 리사이즈 감지
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(renderTable, 100);
    };
    
    // 줌 레벨 변경 감지 (Ctrl+휠)
    const handleZoom = (e: WheelEvent) => {
      if (e.ctrlKey) {
        clearTimeout(zoomTimeout);
        zoomTimeout = setTimeout(renderTable, 200);
      }
    };
    
    // 키보드 줌 감지 (Ctrl + +/- 또는 Ctrl + 0)
    const handleKeyZoom = (e: KeyboardEvent) => {
      if (e.ctrlKey && (e.key === '+' || e.key === '-' || e.key === '=' || e.key === '0')) {
        clearTimeout(zoomTimeout);
        zoomTimeout = setTimeout(renderTable, 200);
      }
    };
    
    // 브라우저 창 포커스 시 리렌더링 (줌 변경 후 창 전환 시)
    const handleFocus = () => {
      setTimeout(renderTable, 100);
    };
    
    // 시각적 변화 감지 (브라우저 메뉴 표시/숨김 등)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setTimeout(renderTable, 100);
      }
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('wheel', handleZoom, { passive: true });
    window.addEventListener('keydown', handleKeyZoom);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearTimeout(resizeTimeout);
      clearTimeout(zoomTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('wheel', handleZoom);
      window.removeEventListener('keydown', handleKeyZoom);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // selectedDate가 undefined가 될 때 표 데이터도 초기화
  useEffect(() => {
    if (!selectedDate) {
      setTableDataState([]);
    }
  }, [selectedDate]);

  // selectedDate가 없으면 표를 렌더링하지 않고 안내 메시지만 표시
  if (!selectedDate) {
    return (
      <div style={{
        width: '100%',
        height: 'calc(100vh - 48px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 28,
        color: '#1976d2',
        fontWeight: 700
      }}>
        날짜를 선택하세요
      </div>
    );
  }
  // 배차완료(주황) 스타일
  if (!document.getElementById('dispatch-done-style')) {
    const style = document.createElement('style');
    style.id = 'dispatch-done-style';
    style.innerHTML = `
      .dispatch-done { 
        background: #ffe0b2 !important; 
        color: #4e342e !important; 
        font-weight: 400 !important; 
        white-space: nowrap !important;
      }
    `;
    document.head.appendChild(style);
  }

  return (
    <div className="dispatch-report-page" style={{ width: '100%', maxWidth: '100%', height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* 상단 UI 업그레이드 - 2줄 구조 (포스트잇 3개 분리) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        
        {/* 1줄: 포스트잇 3개 + 날짜 (왼쪽 정렬, 기존 크기) */}
        <div style={{ display: 'flex', flexDirection: 'row', gap: 12, alignItems: 'flex-start' }}>
          {/* 포스트잇 1 */}
          <div style={{ 
            width: 200,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            padding: '12px 16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <StickyNote2Icon sx={{ fontSize: 14, color: '#6b7280' }} />
              <span style={{ fontWeight: 600, color: '#374151', fontSize: 13 }}>포스트잇1</span>
            </div>
            <textarea
              value={doodle}
              onChange={e => setDoodle(e.target.value)}
              onBlur={e => handleDoodleBlur(e.target.value)}
              rows={5}
              style={{
                width: '100%',
                minHeight: 130,
                maxWidth: '100%',
                boxSizing: 'border-box',
                background: '#fffde7',
                border: '1px solid #ffe082',
                borderRadius: 4,
                fontSize: 13,
                padding: '8px 10px',
                resize: 'vertical',
                color: '#333',
                fontFamily: 'inherit',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                outline: 'none'
              }}
              placeholder="메모1"
            />
          </div>

          {/* 포스트잇 2 */}
          <div style={{ 
            width: 200,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            padding: '12px 16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <StickyNote2Icon sx={{ fontSize: 14, color: '#6b7280' }} />
              <span style={{ fontWeight: 600, color: '#374151', fontSize: 13 }}>포스트잇2</span>
            </div>
            <textarea
              value={doodle2}
              onChange={e => setDoodle2(e.target.value)}
              onBlur={e => handleDoodle2Blur(e.target.value)}
              rows={5}
              style={{
                width: '100%',
                minHeight: 130,
                maxWidth: '100%',
                boxSizing: 'border-box',
                background: '#fffde7',
                border: '1px solid #ffe082',
                borderRadius: 4,
                fontSize: 13,
                padding: '8px 10px',
                resize: 'vertical',
                color: '#333',
                fontFamily: 'inherit',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                outline: 'none'
              }}
              placeholder="메모2"
            />
          </div>

          {/* 포스트잇 3 */}
          <div style={{ 
            width: 200,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            padding: '12px 16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <StickyNote2Icon sx={{ fontSize: 14, color: '#6b7280' }} />
              <span style={{ fontWeight: 600, color: '#374151', fontSize: 13 }}>포스트잇3</span>
            </div>
            <textarea
              value={doodle3}
              onChange={e => setDoodle3(e.target.value)}
              onBlur={e => handleDoodle3Blur(e.target.value)}
              rows={5}
              style={{
                width: '100%',
                minHeight: 130,
                maxWidth: '100%',
                boxSizing: 'border-box',
                background: '#fffde7',
                border: '1px solid #ffe082',
                borderRadius: 4,
                fontSize: 13,
                padding: '8px 10px',
                resize: 'vertical',
                color: '#333',
                fontFamily: 'inherit',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                outline: 'none'
              }}
              placeholder="메모3"
            />
          </div>

          {/* 수출 데이터 테이블 */}
          <div style={{ 
            width: 200,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            padding: '12px 16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 160,
            maxHeight: 160
          }}>
            {/* 수출 테이블 헤더 (고정) */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr auto',
              gap: 4,
              fontSize: 12,
              fontWeight: 600,
              color: '#666',
              marginBottom: 4,
              alignItems: 'center'
            }}>
              <div style={{ padding: '2px 4px', textAlign: 'center' }}>수출</div>
              <div style={{ padding: '2px 4px', textAlign: 'center' }}>넘버</div>
              <button
                onClick={handleAddExportRow}
                style={{
                  background: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: 3,
                  padding: '1px 4px',
                  fontSize: 10,
                  cursor: 'pointer',
                  width: 20,
                  height: 20
                }}
              >
                +
              </button>
            </div>
            
            {/* 수출 데이터 행들 (스크롤 가능) */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto',
              minHeight: 0
            }}>
              {exportData.map((row, index) => (
                <div key={index} style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr auto',
                  gap: 4,
                  marginBottom: 2,
                  alignItems: 'center'
                }}>
                                      <input
                      type="text"
                      value={row.export}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          handleExportDataChange(index, 'export', Number(value) || 0);
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      style={{
                        width: '100%',
                        padding: '2px 4px',
                        border: '1px solid #ddd',
                        borderRadius: 3,
                        fontSize: 13,
                        textAlign: 'center'
                      }}
                    />
                    <input
                      type="text"
                      value={row.number}
                      onChange={(e) => handleExportDataChange(index, 'number', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '2px 4px',
                        border: '1px solid #ddd',
                        borderRadius: 3,
                        fontSize: 13,
                        textAlign: 'center'
                      }}
                    />
                  <button
                    onClick={() => handleDeleteExportRow(index)}
                    style={{
                      background: '#d32f2f',
                      color: 'white',
                      border: 'none',
                      borderRadius: 3,
                      padding: '1px 4px',
                      fontSize: 9,
                      cursor: 'pointer',
                      width: 20,
                      height: 20
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            
            {/* 수출 합계 행 (고정) */}
            {exportData.length > 0 && (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr',
                gap: 4,
                marginTop: 4,
                padding: '4px',
                background: '#f5f5f5',
                borderRadius: 3,
                fontSize: 12,
                fontWeight: 600
              }}>
                <div style={{ textAlign: 'center' }}>
                  {exportData.reduce((sum, row) => sum + (row.export || 0), 0)}
                </div>
                <div style={{ textAlign: 'center' }}></div>
              </div>
            )}
          </div>

          {/* 내수 데이터 테이블 */}
          <div style={{ 
            width: 280,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            padding: '12px 16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 160,
            maxHeight: 160
          }}>
            {/* 내수 테이블 헤더 (고정) */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr 1fr 0.8fr auto',
              gap: 4,
              fontSize: 12,
              fontWeight: 600,
              color: '#666',
              marginBottom: 4,
              alignItems: 'center'
            }}>
              <div style={{ padding: '2px 4px', textAlign: 'center' }}>내수</div>
              <div style={{ padding: '2px 4px', textAlign: 'center' }}>출고</div>
              <div style={{ padding: '2px 4px', textAlign: 'center' }}>넘버</div>
              <div style={{ padding: '2px 4px', textAlign: 'center' }}>주/야</div>
              <button
                onClick={handleAddDomesticRow}
                style={{
                  background: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: 3,
                  padding: '1px 4px',
                  fontSize: 10,
                  cursor: 'pointer',
                  width: 20,
                  height: 20
                }}
              >
                +
              </button>
            </div>
            
            {/* 내수 데이터 행들 (스크롤 가능) */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto',
              minHeight: 0
            }}>
              {domesticData.map((row, index) => (
                <div key={index} style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr 1fr 0.8fr auto',
                  gap: 4,
                  marginBottom: 2,
                  alignItems: 'center'
                }}>
                  <input
                    type="text"
                    value={row.domestic || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d+$/.test(value)) {
                        handleDomesticDataChange(index, 'domestic', Number(value) || 0);
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    style={{
                      width: '100%',
                      padding: '2px 4px',
                      border: '1px solid #ddd',
                      borderRadius: 3,
                      fontSize: 13,
                      textAlign: 'center'
                    }}
                  />
                  <input
                    type="text"
                    value={row.shipment}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d+$/.test(value)) {
                        handleDomesticDataChange(index, 'shipment', Number(value) || 0);
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    style={{
                      width: '100%',
                      padding: '2px 4px',
                      border: '1px solid #ddd',
                      borderRadius: 3,
                      fontSize: 13,
                      textAlign: 'center'
                    }}
                  />
                  <input
                    type="text"
                    value={row.number}
                    onChange={(e) => handleDomesticDataChange(index, 'number', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '2px 4px',
                      border: '1px solid #ddd',
                      borderRadius: 3,
                      fontSize: 13,
                      textAlign: 'center'
                    }}
                  />
                  <input
                    type="text"
                    value={row.dayNight || ''}
                    onChange={(e) => handleDomesticDataChange(index, 'dayNight', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '2px 4px',
                      border: '1px solid #ddd',
                      borderRadius: 3,
                      fontSize: 13,
                      textAlign: 'center'
                    }}
                  />
                  <button
                    onClick={() => handleDeleteDomesticRow(index)}
                    style={{
                      background: '#d32f2f',
                      color: 'white',
                      border: 'none',
                      borderRadius: 3,
                      padding: '1px 4px',
                      fontSize: 9,
                      cursor: 'pointer',
                      width: 20,
                      height: 20
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            
            {/* 내수 합계 행 (고정) */}
            {domesticData.length > 0 && (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr 1fr 0.8fr auto',
                gap: 4,
                marginTop: 4,
                padding: '4px',
                background: '#f5f5f5',
                borderRadius: 3,
                fontSize: 12,
                fontWeight: 600
              }}>
                <div style={{ textAlign: 'center' }}>
                  {domesticData.reduce((sum, row) => sum + (row.domestic || 0), 0)}
                </div>
                <div style={{ textAlign: 'center' }}>
                  {domesticData.reduce((sum, row) => sum + row.shipment, 0)}
                </div>
                <div style={{ textAlign: 'center' }}></div>
                <div style={{ textAlign: 'center' }}></div>
                <div style={{ width: 20 }}></div>
              </div>
            )}
          </div>

          {/* 날짜 (포스트잇과 동일한 크기) */}
          <div style={{ 
            width: 240,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            padding: '12px 16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              <span style={{ fontWeight: 600, color: '#374151', fontSize: 14 }}>날짜</span>
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 130,
              width: '100%'
            }}>
              <span style={{ 
                fontWeight: 700, 
                fontSize: 24, 
                color: '#1976d2',
                textAlign: 'center',
                lineHeight: 1.2
              }}>
                {selectedDate
                  ? selectedDate.split(',').map(date => {
                      const dayKor = getKoreanDayShort(date.trim());
                      return (
                        <div key={date} style={{ marginBottom: 4 }}>
                          {date.trim()}
                          <br />
                          <span style={{ 
                            color: getDayColor(dayKor), 
                            fontWeight: 700,
                            fontSize: 20
                          }}>
                            ({dayKor})
                          </span>
                        </div>
                      );
                    }).reduce((prev, curr, idx) => idx === 0 ? [curr] : [prev, <div key={`sep-${idx}`} style={{ marginBottom: 8 }}>,</div>, curr], [] as any)
                  : '날짜를 선택하세요'}
              </span>
            </div>
          </div>
        </div>

        {/* 2줄: 거래처추가, 배차오더, 수출/내수/유지, 차량찾기, 복구 (한 줄에) */}
        <div style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          
          {/* 거래처 추가 버튼 */}
          <button
            onClick={handleAddCompanyClick}
            style={{
              background: '#fff',
              color: '#222',
              border: '1px solid #d1d5db',
              borderRadius: 4,
              padding: '0 16px',
              fontWeight: 500,
              fontSize: '14px',
              height: 36,
              boxSizing: 'border-box',
              cursor: 'pointer',
              boxShadow: 'none',
              outline: 'none',
              transition: 'background 0.2s',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
            onMouseOver={e => e.currentTarget.style.background = '#f3f4f6'}
            onMouseOut={e => e.currentTarget.style.background = '#fff'}
          >
            <AddIcon sx={{ fontSize: 16 }} />
            거래처 추가
          </button>



          {/* 배차오더/미배차 카드 */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'row', 
            alignItems: 'center', 
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            padding: '8px 12px',
            gap: 8,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <span style={{ fontWeight: 600, color: '#1976d2', fontSize: 13, whiteSpace: 'nowrap' }}>배차오더</span>
            <span style={{ 
              fontWeight: 700, 
              color: '#1976d2', 
              fontSize: 14,
              padding: '2px 8px',
              background: '#f0f8ff',
              borderRadius: 4,
              minWidth: 20,
              textAlign: 'center'
            }}>
              {orderCount}
            </span>
            <span style={{ fontWeight: 600, color: '#d32f2f', fontSize: 13, whiteSpace: 'nowrap' }}>미배차</span>
            <span style={{ 
              fontWeight: 700, 
              color: '#d32f2f', 
              fontSize: 14,
              padding: '2px 8px',
              background: '#fff5f5',
              borderRadius: 4,
              minWidth: 20,
              textAlign: 'center'
            }}>
              {notAssignedCount}
            </span>
          </div>

          {/* 수출 그룹 */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'row', 
            alignItems: 'center', 
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            padding: '8px 12px',
            gap: 8,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <IosShareIcon sx={{ fontSize: 16, color: '#6b7280' }} />
            <span style={{ fontWeight: 600, color: '#1976d2', fontSize: 13, whiteSpace: 'nowrap' }}>수출</span>
            <input 
              value={exportValue} 
              onChange={e => setExportValue(e.target.value)} 
              onBlur={e => handleSummaryBlur('exportValue', e.target.value)} 
              style={{ 
                width: 50, 
                padding: '4px 8px', 
                borderRadius: 4, 
                border: '1px solid #d1d5db', 
                fontSize: 13, 
                textAlign: 'center',
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }} 
              placeholder="숫자" 
            />
            <span style={{ fontWeight: 600, color: '#1976d2', fontSize: 13, whiteSpace: 'nowrap' }}>수출메모</span>
            <input 
              value={exportMemo} 
              onChange={e => setExportMemo(e.target.value)} 
              onBlur={e => handleSummaryBlur('exportMemo', e.target.value)} 
              style={{ 
                width: 80, 
                padding: '4px 8px', 
                borderRadius: 4, 
                border: '1px solid #d1d5db', 
                fontSize: 13,
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }} 
              placeholder="수출메모" 
            />
          </div>

          {/* 내수 그룹 */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'row', 
            alignItems: 'center', 
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            padding: '8px 12px',
            gap: 8,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <HomeIcon sx={{ fontSize: 16, color: '#6b7280' }} />
            <span style={{ fontWeight: 600, color: '#1976d2', fontSize: 13, whiteSpace: 'nowrap' }}>내수</span>
            <input 
              value={domesticValue} 
              onChange={e => setDomesticValue(e.target.value)} 
              onBlur={e => handleSummaryBlur('domesticValue', e.target.value)} 
              style={{ 
                width: 50, 
                padding: '4px 8px', 
                borderRadius: 4, 
                border: '1px solid #d1d5db', 
                fontSize: 13, 
                textAlign: 'center',
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }} 
              placeholder="숫자" 
            />
            <span style={{ fontWeight: 600, color: '#1976d2', fontSize: 13, whiteSpace: 'nowrap' }}>내수메모</span>
            <input 
              value={domesticMemo} 
              onChange={e => setDomesticMemo(e.target.value)} 
              onBlur={e => handleSummaryBlur('domesticMemo', e.target.value)} 
              style={{ 
                width: 80, 
                padding: '4px 8px', 
                borderRadius: 4, 
                border: '1px solid #d1d5db', 
                fontSize: 13,
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }} 
              placeholder="내수메모" 
            />
          </div>

          {/* 유지 그룹 */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'row', 
            alignItems: 'center', 
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            padding: '8px 12px',
            gap: 8,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <LocalGasStationIcon sx={{ fontSize: 16, color: '#6b7280' }} />
            <span style={{ fontWeight: 600, color: '#1976d2', fontSize: 13, whiteSpace: 'nowrap' }}>유지</span>
            <input 
              value={oilValue} 
              onChange={e => setOilValue(e.target.value)} 
              onBlur={e => handleSummaryBlur('oilValue', e.target.value)} 
              style={{ 
                width: 50, 
                padding: '4px 8px', 
                borderRadius: 4, 
                border: '1px solid #d1d5db', 
                fontSize: 13, 
                textAlign: 'center',
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }} 
              placeholder="숫자" 
            />
            <span style={{ fontWeight: 600, color: '#1976d2', fontSize: 13, whiteSpace: 'nowrap' }}>유지메모</span>
            <input 
              value={oilMemo} 
              onChange={e => setOilMemo(e.target.value)} 
              onBlur={e => handleSummaryBlur('oilMemo', e.target.value)} 
              style={{ 
                width: 80, 
                padding: '4px 8px', 
                borderRadius: 4, 
                border: '1px solid #d1d5db', 
                fontSize: 13,
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }} 
              placeholder="유지메모" 
            />
          </div>

          {/* 차량번호 끝 4자리 검색 (전화 문의 시 빠르게 행 찾기) */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              padding: '8px 12px',
              gap: 8,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}
            title="예: 부산93아1234 → 숫자만 1234 입력 시 해당 행으로 이동·강조"
          >
            <span style={{ fontWeight: 600, color: '#1976d2', fontSize: 13, whiteSpace: 'nowrap' }}>차량찾기</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={vehiclePlateSuffix}
              onChange={(e) => setVehiclePlateSuffix(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
              maxLength={4}
              style={{
                width: 72,
                padding: '6px 8px',
                borderRadius: 4,
                border: '1px solid #d1d5db',
                fontSize: 15,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textAlign: 'center',
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
              }}
              placeholder="끝4자리"
            />
          </div>

          {/* 복구 + 전체화면 버튼 그룹 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              disabled={!isSingleDateToday || restoreLoading}
              onClick={async () => {
                if (!isSingleDateToday || !selectedDate) return;
                try {
                  setRestoreOpen(true);
                  setRestoreLoading(true);
                  setRestoreError(null);
                  const res = await fetch(`/api/dispatch-backup/today-snapshots?date=${encodeURIComponent(selectedDate)}`);
                  const data = await res.json();
                  if (!res.ok) throw new Error(data?.error || '스냅샷 목록 조회 실패');
                  setSnapshots(Array.isArray(data.snapshots) ? data.snapshots : []);
                  setSelectedSnapshotId((Array.isArray(data.snapshots) && data.snapshots[0]?.id) || '');
                } catch (e: any) {
                  console.error(e);
                  setRestoreError(e?.message || '스냅샷 목록 조회 중 오류가 발생했습니다.');
                } finally {
                  setRestoreLoading(false);
                }
              }}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid #f59e0b',
                background: !isSingleDateToday || restoreLoading ? '#fff7ed' : '#fffbeb',
                color: '#b45309',
                fontWeight: 600,
                fontSize: 13,
                cursor: !isSingleDateToday || restoreLoading ? 'not-allowed' : 'pointer',
                opacity: !isSingleDateToday ? 0.6 : 1,
                whiteSpace: 'nowrap',
              }}
              title={isSingleDateToday ? '오늘 날짜의 배차 데이터를 스냅샷에서 복구' : '오늘 날짜 한 개를 선택했을 때만 사용 가능합니다'}
            >
              복구
            </button>
            <FullScreenButton isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
          </div>
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowX: 'auto' }}>
        <HotTable
          ref={hotRef}
          data={tableDataState}
          colHeaders={COLUMN_HEADERS}
          columns={COLUMN_HEADERS.map((header, idx) => {
            if (header === '청구운송비' || header === '지불운송비') {
              return { type: 'numeric', numericFormat: { pattern: '#,##0' } };
            }
            if (header === '연락처') {
              return { type: 'text' };
            }
            if (header === '문자') {
              return { readOnly: true, renderer: smsRenderer as any };
            }
            return idx === IDX.company ? { readOnly: true } : { readOnly: false };
          })}
          colWidths={colWidths}
          rowHeaders={true}
          width="100%"
          height="100%"
          style={{ maxWidth: '100%' }}
          licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
          stretchH="all"
          fixedRowsTop={0}
          fixedColumnsLeft={1}
          manualRowResize={true}
          manualColumnResize={true}
          autoColumnSize={false}
          autoRowSize={false}
          manualColumnMove={true}
          className="htCenter"
          afterChange={handleAfterChange}
          cells={cells}
          contextMenu={customContextMenu}
          afterOnCellContextMenu={suppressRouteMemoHover}
          afterContextMenuHide={resumeRouteMemoHover}
          afterPaste={handleAfterPaste}
          afterLoadData={() => {
            setTimeout(() => {
              if (hotRef.current?.hotInstance) {
                hotRef.current.hotInstance.render();
              }
            }, 100);
          }}
          afterUpdateSettings={() => {
            setTimeout(() => {
              if (hotRef.current?.hotInstance) {
                hotRef.current.hotInstance.render();
              }
            }, 100);
          }}
        />
      </div>
      {showCompanyModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.2)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 'min(1000px, 90vw)', boxShadow: '0 2px 16px rgba(0,0,0,0.15)', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'stretch', minHeight: '70vh', maxHeight: '80vh', height: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>추가할 거래처를 선택하세요</h3>
              <div style={{ color: '#6b7280', fontSize: 13 }}>
                {(() => {
                  const q = companySearch.trim().toLowerCase();
                  const filtered = companyOptions.filter(c => q === '' ? true : c.toLowerCase().includes(q));
                  return `총 ${filtered.length.toLocaleString()}개`;
                })()}
              </div>
            </div>
            {/* 검색 입력 */}
            <div style={{ marginBottom: 12 }}>
              <input
                type="text"
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                placeholder="거래처 검색 (한글/영문 일부 입력)"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            {/* 거래처 리스트: 그리드 다단 배치 */}
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '60vh', overflowY: 'auto', width: '100%', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
              {(() => {
                const q = companySearch.trim().toLowerCase();
                const filtered = companyOptions.filter(c => q === '' ? true : c.toLowerCase().includes(q));
                if (filtered.length === 0) {
                  return <li style={{ color: '#888' }}>검색 결과가 없습니다.</li>;
                }
                return filtered.map((c) => (
                  <li key={c}>
                    <button
                      onClick={() => handleSelectCompany(c)}
                      style={{
                        width: '100%',
                        padding: '10px 0',
                        border: '1px solid #e3f2fd',
                        borderRadius: 8,
                        background: '#e9f3ff',
                        color: '#1976d2',
                        fontWeight: 700,
                        fontSize: 15,
                        cursor: 'pointer'
                      }}
                    >
                      {c}
                    </button>
                </li>
                ));
              })()}
            </ul>
            {/* 닫기 버튼을 리스트 아래에 항상 보이게 */}
            <button onClick={() => setShowCompanyModal(false)} style={{ marginTop: 14, background: '#eee', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 500, cursor: 'pointer', width: '100%' }}>닫기</button>
          </div>
        </div>
      )}

      {/* 배차일보 복구 모달 */}
      {restoreOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 11000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => {
            if (restoreLoading) return;
            setRestoreOpen(false);
            setRestoreError(null);
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              maxWidth: 520,
              width: '100%',
              maxHeight: '80vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              style={{
                padding: '14px 18px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 600, color: '#111' }}>배차일보 복구</span>
              <button
                type="button"
                onClick={() => {
                  if (restoreLoading) return;
                  setRestoreOpen(false);
                  setRestoreError(null);
                }}
                style={{
                  padding: '4px 10px',
                  fontSize: 13,
                  borderRadius: 6,
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  cursor: restoreLoading ? 'not-allowed' : 'pointer',
                  color: '#374151',
                }}
              >
                닫기
              </button>
            </div>
            <div style={{ padding: 16, flex: 1, overflow: 'auto' }}>
              {restoreError && (
                <div
                  style={{
                    marginBottom: 12,
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: '1px solid #fecaca',
                    background: '#fef2f2',
                    color: '#b91c1c',
                    fontSize: 13,
                  }}
                >
                  {restoreError}
                </div>
              )}
              <p style={{ fontSize: 13, color: '#4b5563', marginBottom: 12, lineHeight: 1.6 }}>
                오늘 날짜(<strong>{selectedDate}</strong>)의 배차 데이터를 선택한 시점의 스냅샷으로 <strong>완전히 덮어씁니다</strong>.
                현재 화면의 수정 내용은 모두 사라지고, 선택한 시각 기준의 데이터로 되돌아갑니다.
              </p>
              {restoreLoading ? (
                <div style={{ padding: 24, textAlign: 'center', fontSize: 14, color: '#6b7280' }}>
                  스냅샷 목록을 불러오는 중입니다...
                </div>
              ) : snapshots.length === 0 ? (
                <div style={{ padding: 16, fontSize: 14, color: '#6b7280' }}>
                  사용 가능한 스냅샷이 없습니다. 서버에서 백업이 아직 생성되지 않았을 수 있습니다.
                </div>
              ) : (
                <div style={{ maxHeight: 260, overflowY: 'auto', marginTop: 8 }}>
                  {snapshots.map(s => {
                    const createdAt = s.createdAt ? new Date(s.createdAt) : null;
                    const timeLabel = createdAt
                      ? createdAt.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })
                      : s.id.split('_')[1] || s.id;
                    return (
                      <label
                        key={s.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 10px',
                          borderRadius: 8,
                          border: '1px solid #e5e7eb',
                          marginBottom: 8,
                          cursor: 'pointer',
                          background: selectedSnapshotId === s.id ? '#eff6ff' : '#fff',
                        }}
                        onClick={() => setSelectedSnapshotId(s.id)}
                      >
                        <input
                          type="radio"
                          checked={selectedSnapshotId === s.id}
                          onChange={() => setSelectedSnapshotId(s.id)}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{timeLabel} 스냅샷</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            행 수: {s.rowCount.toLocaleString()}건
                            {s.createdAt && (
                              <span style={{ marginLeft: 8, color: '#9ca3af' }}>
                                ({new Date(s.createdAt).toLocaleString('ko-KR')})
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div
              style={{
                padding: '12px 16px',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={() => {
                  if (restoreLoading) return;
                  setRestoreOpen(false);
                  setRestoreError(null);
                }}
                style={{
                  padding: '8px 14px',
                  borderRadius: 6,
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  cursor: restoreLoading ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                }}
              >
                취소
              </button>
              <button
                type="button"
                disabled={restoreLoading || !selectedSnapshotId}
                onClick={async () => {
                  if (!selectedSnapshotId || !selectedDate) return;
                  if (
                    !window.confirm(
                      `정말로 ${selectedDate}의 배차 데이터를 스냅샷(${selectedSnapshotId}) 기준으로 복구하시겠습니까?\n현재 내용은 모두 사라집니다.`
                    )
                  ) {
                    return;
                  }
                  try {
                    setRestoreLoading(true);
                    setRestoreError(null);
                    const res = await fetch('/api/dispatch-backup/restore', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ date: selectedDate, snapshotId: selectedSnapshotId }),
                    });
                    const data = await res.json();
                    if (!res.ok) throw new Error(data?.error || '복구 실패');
                    // 성공 시 다시 조회
                    setRestoreOpen(false);
                    setRestoreLoading(false);
                    setLoading(true);
                    debouncedFetch();
                    window.alert('배차일보 복구가 완료되었습니다.');
                  } catch (e: any) {
                    console.error(e);
                    setRestoreLoading(false);
                    setRestoreError(e?.message || '복구 중 오류가 발생했습니다.');
                  }
                }}
                style={{
                  padding: '8px 18px',
                  borderRadius: 6,
                  border: 'none',
                  background: restoreLoading || !selectedSnapshotId ? '#f97316' : '#ea580c',
                  color: '#fff',
                  cursor: restoreLoading || !selectedSnapshotId ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                {restoreLoading ? '복구 중...' : '선택한 스냅샷으로 복구'}
              </button>
            </div>
          </div>
        </div>
      )}
      {routeMemoHover && (
        <div
          className="route-memo-hover-popup"
          style={{
            position: 'fixed',
            left: routeMemoHover.left,
            top: routeMemoHover.top,
            zIndex: 90,
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            suppressRouteMemoHover();
          }}
        >
          <div className="route-memo-hover-popup__header">
            <span>운송구간 메모</span>
            <button
              type="button"
              className="route-memo-hover-popup__close"
              aria-label="메모 닫기"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => dismissRouteMemoHover()}
            >
              ×
            </button>
          </div>
          <div className="route-memo-hover-popup__body">{routeMemoHover.text}</div>
          <div className="route-memo-hover-popup__actions">
            <button
              type="button"
              className="route-memo-hover-popup__btn route-memo-hover-popup__btn--secondary"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => copyRouteMemoText(routeMemoHover.text)}
            >
              복사
            </button>
            <button
              type="button"
              className="route-memo-hover-popup__btn"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => openRouteMemoEditor(routeMemoHover.row)}
            >
              편집
            </button>
          </div>
        </div>
      )}
      <Dialog
        open={!!routeMemoEdit}
        onClose={() => !routeMemoSaving && setRouteMemoEdit(null)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEntered: focusRouteMemoInput,
        }}
      >
        <DialogTitle>운송구간 메모</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={routeMemoInputRef}
            autoFocus
            multiline
            minRows={5}
            maxRows={12}
            fullWidth
            placeholder="운송구간 셀에 붙는 메모입니다. 표에는 짧게만 보이고, 마우스를 올리면 전체 내용이 표시됩니다."
            value={routeMemoEdit?.text ?? ''}
            onChange={(e) => {
              const text = e.target.value;
              setRouteMemoEdit((prev) => (prev ? { ...prev, text } : prev));
            }}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button
            color="error"
            disabled={
              routeMemoSaving ||
              !routeMemoEdit ||
              (
                !String(routeMemoEdit.text ?? '').trim() &&
                !String(tableDataState[routeMemoEdit.row]?.[IDX.routeMemo] ?? '').trim()
              )
            }
            onClick={async () => {
              if (!routeMemoEdit) return;
              if (!window.confirm('운송구간 메모를 삭제할까요?')) return;
              setRouteMemoSaving(true);
              const ok = await saveRouteMemo(routeMemoEdit.row, '');
              setRouteMemoSaving(false);
              if (ok) setRouteMemoEdit(null);
            }}
          >
            삭제
          </Button>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="outlined"
              disabled={routeMemoSaving || !String(routeMemoEdit?.text ?? '').trim()}
              onClick={() => {
                if (!routeMemoEdit) return;
                copyRouteMemoText(routeMemoEdit.text);
              }}
            >
              클립보드 복사
            </Button>
            <Button onClick={() => setRouteMemoEdit(null)} disabled={routeMemoSaving}>
              취소
            </Button>
            <Button
              variant="contained"
              disabled={routeMemoSaving}
              onClick={async () => {
                if (!routeMemoEdit) return;
                setRouteMemoSaving(true);
                const ok = await saveRouteMemo(routeMemoEdit.row, routeMemoEdit.text.trim());
                setRouteMemoSaving(false);
                if (ok) setRouteMemoEdit(null);
              }}
            >
              {routeMemoSaving ? '저장 중...' : '저장'}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
      <SmsModal
        open={smsOpen}
        onClose={() => setSmsOpen(false)}
        phoneNumber={smsPhone}
        defaultMessage={smsDefaultMsg}
        title={`${(tableDataState.find(r=>r[IDX.id]===smsRowId)?.[IDX.company]||'').toString()} / ${(tableDataState.find(r=>r[IDX.id]===smsRowId)?.[IDX.route]||'').toString()}`}
        templates={(() => {
          const row = tableDataState.find(r => r[IDX.id] === smsRowId);
          const company = row ? String(row[IDX.company] || '').trim() : '';
          // 템플릿을 별도 API에서 가져오면 좋지만, 간단히 런타임 fetch 없이 표시하려면 빈 배열 리턴
          // 이 자리에 동기 데이터가 없다면 상위에서 상태로 관리해 전달하는 방식이 필요
          return (window as any).__dispatchCompanyTemplates?.[company] || [];
        })()}
        vehicleNumber={(tableDataState.find(r=>r[IDX.id]===smsRowId)?.[IDX.vehicle]||'').toString()}
        cargo={(tableDataState.find(r=>r[IDX.id]===smsRowId)?.[IDX.cargo]||'').toString()}
        showSentListButton={smsRowId ? !!smsStatus[smsRowId] : false}
        onOpenSentList={openDispatchSmsSentList}
        onSendMms={async (phone, message, fileIds) => {
          try {
            const row = tableDataState.find(r => r[IDX.id] === smsRowId);
            const vehicleNumber = row ? String(row[IDX.vehicle] || '').trim() : '';
            const res = await fetch('/api/sms/mms/send', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                to: phone,
                content: message,
                fileIds,
                userId: auth.currentUser?.uid,
                refRowId: smsRowId,
                date: selectedDate,
                vehicleNumber: vehicleNumber || undefined
              })
            });
            if (!res.ok) return false;
            // 전송 성공 시 해당 행 sms=true 저장 (SMS와 동일하게 처리)
            if (smsRowId && selectedDate) {
              try {
                await fetch(`/api/dispatch-reports/${selectedDate}/rows/${smsRowId}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ sms: true, userId: auth.currentUser?.uid })
                });
                setSmsStatus(prev => ({ ...prev, [smsRowId]: true }));
              } catch {}
            }
            return true;
          } catch (e) {
            return false;
          }
        }}
        onSend={async (phone, message) => {
          try {
            const row = tableDataState.find(r => r[IDX.id] === smsRowId);
            const vehicleNumber = row ? String(row[IDX.vehicle] || '').trim() : '';
            const res = await fetch('/api/sms/send', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                to: phone,
                content: message,
                refRowId: smsRowId,
                date: selectedDate,
                vehicleNumber: vehicleNumber || undefined,
                userId: auth.currentUser?.uid
              })
            });
            if (!res.ok) return false;
            // 전송 성공 시 해당 행 sms=true로 서버에 저장 (조건부 필드)
            if (smsRowId && selectedDate) {
              try {
                await fetch(`/api/dispatch-reports/${selectedDate}/rows/${smsRowId}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ sms: true, userId: auth.currentUser?.uid })
                });
                setSmsStatus(prev => ({ ...prev, [smsRowId]: true }));
              } catch {}
            }
            return true;
          } catch (e) {
            return false;
          }
        }}
      />
      <Dialog open={smsSentListOpen} onClose={() => setSmsSentListOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>보낸 문자 목록</DialogTitle>
        <DialogContent dividers>
          {smsSentListSubtitle && (
            <div style={{ fontSize: 13, color: '#616161', marginBottom: 12 }}>{smsSentListSubtitle}</div>
          )}
          {smsSentListLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
              <CircularProgress size={28} />
            </div>
          )}
          {smsSentListError && !smsSentListLoading && (
            <Alert severity="error" sx={{ mb: 1 }}>{smsSentListError}</Alert>
          )}
          {!smsSentListLoading && !smsSentListError && smsSentListItems.length === 0 && (
            <div style={{ color: '#757575', fontSize: 14 }}>표시할 발송 이력이 없습니다.</div>
          )}
          {!smsSentListLoading && smsSentListItems.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 360, overflow: 'auto' }}>
              {smsSentListItems.map((item) => {
                const t =
                  typeof item.sendAt === 'number'
                    ? item.sendAt
                    : typeof item.createdAtMs === 'number'
                      ? item.createdAtMs
                      : item.createdAt?.seconds
                        ? item.createdAt.seconds * 1000
                        : 0;
                const when = t ? new Date(t).toLocaleString('ko-KR') : '—';
                const kind = item.type === 'MMS' ? 'MMS' : item.type === 'LMS' ? 'LMS' : 'SMS';
                return (
                  <li key={item.id} style={{ marginBottom: 12, fontSize: 13 }}>
                    <div style={{ color: '#424242', fontWeight: 600 }}>{when} · {kind}</div>
                    {item.dispatchDate && (
                      <div style={{ fontSize: 12, color: '#757575' }}>배차일: {item.dispatchDate}</div>
                    )}
                    <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{String(item.content || '')}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
        <DialogActions sx={{ gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            disabled={
              smsSentListLoading ||
              smsSentListError != null ||
              smsSentListItems.length === 0 ||
              !String(smsSentListItems[0]?.content ?? '').trim()
            }
            onClick={async () => {
              const text = String(smsSentListItems[0]?.content ?? '').trim();
              if (!text) return;
              try {
                await navigator.clipboard.writeText(text);
                setSmsSentListCopySnackbar(true);
              } catch {
                try {
                  const ta = document.createElement('textarea');
                  ta.value = text;
                  ta.style.position = 'fixed';
                  ta.style.left = '-9999px';
                  document.body.appendChild(ta);
                  ta.focus();
                  ta.select();
                  document.execCommand('copy');
                  document.body.removeChild(ta);
                  setSmsSentListCopySnackbar(true);
                } catch {
                  window.alert('클립보드 복사에 실패했습니다.');
                }
              }
            }}
          >
            클립보드 복사
          </Button>
          <Button onClick={() => setSmsSentListOpen(false)} variant="contained">
            닫기
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={smsSentListCopySnackbar}
        autoHideDuration={2200}
        onClose={() => setSmsSentListCopySnackbar(false)}
        message="최근 문자 내용을 복사했습니다"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Snackbar
        open={routeMemoCopySnackbar}
        autoHideDuration={2200}
        onClose={() => setRouteMemoCopySnackbar(false)}
        message="운송구간 메모를 복사했습니다"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      {vehicleAutoList.length > 0 && vehicleAutoRow != null && vehicleAutoCol === 1 && vehicleAutoBoxPos && (
        <div style={{
          position: 'fixed',
          left: vehicleAutoBoxPos.left,
          top: vehicleAutoBoxPos.top,
          background: '#fff',
          border: '1px solid #1976d2',
          borderRadius: 6,
          zIndex: 1000,
          minWidth: 220,
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
        }}>
          {/* 헤더 영역 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            borderBottom: '1px solid #eee',
            background: '#f8f9fa'
          }}>
            <span style={{ fontWeight: 600, color: '#1976d2', fontSize: 14 }}>차량 선택</span>
            <button
              onClick={() => {
                setVehicleAutoList([]);
                setVehicleAutoRow(null);
                setVehicleAutoCol(null);
                setVehicleAutoBoxPos(null);
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 18,
                cursor: 'pointer',
                color: '#666',
                padding: '0',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              title="닫기"
            >
              ×
            </button>
          </div>
          {/* 목록 영역 */}
          {vehicleAutoList.map((v, idx) => (
            <div
              key={v.vehicleNumber + idx}
              style={{ 
                padding: '8px 12px', 
                cursor: 'pointer', 
                borderBottom: idx < vehicleAutoList.length - 1 ? '1px solid #eee' : 'none',
                color: '#1976d2',
                fontWeight: 600
              }}
              onMouseDown={() => handleSelectVehicleAuto(v)}
            >
              {v.vehicleNumber} 
              <span style={{ color: '#888', fontWeight: 400, fontSize: 13 }}>
                ({v.driverName || ''})
              </span>
            </div>
          ))}
        </div>
      )}
      {pasteError && (
        <div style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          background: '#d32f2f',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          animation: 'fadeIn 0.3s ease-in-out'
        }}>
          {pasteError}
        </div>
      )}
      {routeSuggestions.length > 0 && routeDropdownRow !== null && routeDropdownPos && (
        <div style={{
          position: 'fixed',
          left: routeDropdownPos.left,
          top: routeDropdownPos.top,
          background: '#fff',
          border: '1px solid #1976d2',
          borderRadius: 6,
          zIndex: 1000,
          minWidth: 220,
          boxShadow: '0 2px 8px rgba(30,60,90,0.10)'
        }}>
          {/* 헤더 영역 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            borderBottom: '1px solid #eee',
            background: '#f8f9fa'
          }}>
            <span style={{ fontWeight: 600, color: '#1976d2', fontSize: 14 }}>운송구간 선택</span>
            <button
              onClick={() => {
                setRouteSuggestions([]);
                setRouteDropdownRow(null);
                setRouteDropdownPos(null);
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 18,
                cursor: 'pointer',
                color: '#666',
                padding: '0',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              title="닫기"
            >
              ×
            </button>
          </div>
          {/* 목록 영역 */}
          {routeSuggestions.map((routeObj: any, idx: number) => (
            <div
              key={routeObj.route + idx}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: idx < routeSuggestions.length - 1 ? '1px solid #eee' : 'none',
                color: '#1976d2',
                fontWeight: 600
              }}
              onMouseDown={() => handleSelectRouteSuggestion(routeDropdownRow, routeObj)}
            >
              {routeObj.route}
              {routeObj.distance && (
                <span style={{ color: '#888', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
                  {routeObj.distance}km
                </span>
              )}
              {routeObj.weight && (
                <span style={{ color: '#888', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
                  {routeObj.weight}톤
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      {routeAutoList.length > 0 && routeAutoRow != null && routeAutoCol === 2 && routeAutoBoxPos && (
        <div style={{
          position: 'fixed',
          left: routeAutoBoxPos.left,
          top: routeAutoBoxPos.top,
          background: '#fff',
          border: '1px solid #1976d2',
          borderRadius: 6,
          zIndex: 1000,
          minWidth: 220,
          boxShadow: '0 2px 8px rgba(30,60,90,0.10)'
        }}>
          {/* 헤더 영역 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            borderBottom: '1px solid #eee',
            background: '#f8f9fa'
          }}>
            <span style={{ fontWeight: 600, color: '#1976d2', fontSize: 14 }}>운송구간 자동완성</span>
            <button
              onClick={() => {
                setRouteAutoList([]);
                setRouteAutoRow(null);
                setRouteAutoCol(null);
                setRouteAutoBoxPos(null);
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 18,
                cursor: 'pointer',
                color: '#666',
                padding: '0',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              title="닫기"
            >
              ×
            </button>
          </div>
          {/* 목록 영역 */}
          {routeAutoList.map((routeObj: any, idx: number) => (
            <div
              key={routeObj.route + idx}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: idx < routeAutoList.length - 1 ? '1px solid #eee' : 'none',
                color: '#1976d2',
                fontWeight: 600
              }}
              onMouseDown={() => handleSelectRouteAuto(routeObj)}
            >
              {routeObj.route}
            </div>
          ))}
        </div>
      )}
      {rateAutoList.length > 0 && rateAutoRow != null && rateAutoCol === 5 && rateAutoBoxPos && (
        <div style={{
          position: 'fixed',
          left: rateAutoBoxPos.left,
          top: rateAutoBoxPos.top,
          background: '#fff',
          border: '1px solid #1976d2',
          borderRadius: 6,
          zIndex: 1000,
          minWidth: 220,
          boxShadow: '0 2px 8px rgba(30,60,90,0.10)'
        }}>
          {/* 헤더 영역 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            borderBottom: '1px solid #eee',
            background: '#f8f9fa'
          }}>
            <span style={{ fontWeight: 600, color: '#1976d2', fontSize: 14 }}>운임 선택</span>
            <button
              onClick={() => {
                setRateAutoList([]);
                setRateAutoRow(null);
                setRateAutoCol(null);
                setRateAutoBoxPos(null);
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 18,
                cursor: 'pointer',
                color: '#666',
                padding: '0',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              title="닫기"
            >
              ×
            </button>
          </div>
          {/* 목록 영역 */}
          {rateAutoList.map((rate: any, idx: number) => {
            const colorStyle = getSegmentColor(rate.fuelSegment);
            return (
              <div
                key={rate.fuelSegment + idx}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderBottom: idx < rateAutoList.length - 1 ? '1px solid #eee' : 'none',
                  fontWeight: 600,
                  background: colorStyle.background,
                  color: colorStyle.color
                }}
                onMouseDown={() => handleSelectRateAuto(rate)}
              >
                {rate.fuelSegment} : {rate.billingAmount?.toLocaleString()}원
              </div>
            );
          })}
        </div>
      )}
      {(loading || summaryLoading) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.95)',
            padding: 40,
            borderRadius: 16,
            boxShadow: '0 2px 16px rgba(0,0,0,0.10)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            fontSize: 22,
            fontWeight: 700,
            color: '#1976d2'
          }}>
            <div className="spinner" style={{ marginBottom: 18 }}>
              <svg width="48" height="48" viewBox="0 0 50 50">
                <circle cx="25" cy="25" r="20" fill="none" stroke="#1976d2" strokeWidth="6" strokeDasharray="31.415, 31.415" transform="rotate(72.5042 25 25)">
                  <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite"/>
                </circle>
              </svg>
            </div>
            로딩 중...
          </div>
        </div>
      )}
    </div>
  );
};

export default DispatchReportPage; 